import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import {
  getTrip,
  getReviewForTrip,
  createReview,
  updateReview,
  submitReview,
  deleteReview,
  getMediaGallery,
  presignMediaUpload,
  finalizeMediaUpload,
  deleteMedia,
  updateMedia,
  getMediaLimits,
  getRewardsPreview,
  getCommunityStats,
  uploadFileToS3WithProgress,
  publishTrip
} from "../services/api";
import { mapServerIssuesToForm } from "../utils/validation";
import {
  openGoogleReviewHandoffWindow,
  completeGoogleReviewHandoff,
  cancelGoogleReviewHandoff,
  copyReviewText,
} from "../utils/googleReview";
import Footer from "../components/layout/Footer";
import ReviewThankYou from "../components/review/ReviewThankYou";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "react-toastify";
import { Star, Camera, Sparkles, Building, Car, Utensils, ThumbsUp, ThumbsDown, Gift, AlertTriangle, Compass, Check, Laugh, Smile, Meh, Frown, Lightbulb, Users, ArrowLeft, ArrowRight, Map, Video } from "lucide-react";
import * as Sentry from "@sentry/react";
import { MediaError, CommunityError, PaymentError } from "../errors/sentryErrors";

// Used only until GET /api/media/limits answers, and as the floor if it never does —
// an upload form that cannot validate at all is worse than one validating against
// slightly stale numbers. The server is authoritative; never render these directly
// once `limits` has loaded.
const FALLBACK_MEDIA_LIMITS = {
  photosPerTrip: 20,
  videosPerTrip: 5,
  coverPhotosPerTrip: 1,
  maxPhotoBytes: 15 * 1024 * 1024,
  maxVideoBytes: 200 * 1024 * 1024,
  maxVideoSeconds: 120,
  photoCategories: ["cover", "destination", "hotel", "food", "attraction"],
  videoCategories: ["travel"],
};

const TOTAL_STEPS = 4;

// Mirrors mediaService.CAPTION_MAX — the server rejects anything longer.
const CAPTION_MAX = 500;

// Auto-save fires this long after the user stops typing — never on a fixed clock.
const AUTOSAVE_DEBOUNCE_MS = 1500;

// Shapes the form values into the draft payload the API expects. Kept pure and at
// module scope so the dirty-check and the request compare byte-identical objects —
// if these ever diverged we'd either save on every keystroke or never save at all.
// Every integer 1-5 field the API accepts, in one list so the payload builder, the
// form defaults and the hydration path cannot drift apart as fields are added.
const STAR_FIELDS = [
  "overallRating",
  "hotelRating",
  "transportRating",
  "foodRating",
  "aiHelpfulness",
  "itinerarySatisfaction",
  "valueForMoney",
  "itineraryEasyToFollow",
];

// The free-text fields the API runs through the admin bad-words filter (its
// TEXT_FIELDS). A moderation error can only ever land on one of these, and the
// form has no resolver, so any error on them came from the server — which makes
// a save the server accepted the exact moment they are safe to clear.
const MODERATED_TEXT_FIELDS = [
  "description",
  "bestExperience",
  "improvements",
  "whatWentWrong",
];

// `aiHelpfulness` is in the API's RATING_FIELDS — an integer 1-5, and the mean the
// AI Helpfulness KPI dashboard reports. The design mock offers four choices; four
// buckets cannot express a 1-5 scale, and squashing it would both distort the
// north-star metric and make it incomparable with every review already collected.
// So the wording is the mock's, on all five points.
const AI_HELPFULNESS_OPTIONS = [
  { value: 1, label: "Not Helpful" },
  { value: 2, label: "A Little Helpful" },
  { value: 3, label: "Somewhat Helpful" },
  { value: 4, label: "Very Helpful" },
  { value: 5, label: "Life Changing" },
];

// Values must match reviewService.ENUM_FIELDS byte for byte — the server rejects
// anything else. The labels are ours to change freely.
const ENUM_FIELDS = {
  dealPerception: {
    question: "Did you get the best deal through Roam My Way?",
    options: [
      { value: "definitely", label: "Definitely", Icon: Laugh },
      { value: "probably", label: "Probably", Icon: Smile },
      { value: "not_sure", label: "Not sure", Icon: Meh },
      { value: "no", label: "No", Icon: Frown },
    ],
  },
  // `dealPerception` renders as faces rather than plain pills, so its options carry
  // an icon. The values are still the server's allow-list, untouched.
  vegetarianOptions: {
    question: "Were there enough vegetarian options?",
    options: [
      { value: "yes", label: "Yes, plenty" },
      { value: "somewhat", label: "Somewhat" },
      { value: "no", label: "No" },
      { value: "na", label: "Not applicable" },
    ],
  },
  itineraryCoverage: {
    question: "Did you cover everything in the itinerary?",
    options: [
      { value: "yes_all", label: "Yes, all of it" },
      { value: "most", label: "Most of it" },
      { value: "some", label: "Some of it" },
      { value: "no", label: "No" },
    ],
  },
  itineraryPace: {
    question: "How was the pace?",
    options: [
      { value: "too_relaxed", label: "Too relaxed" },
      { value: "perfect", label: "Just right" },
      { value: "too_busy", label: "Too busy" },
    ],
  },
};

const buildDraftPayload = (values) => {
  const payload = { ...values };

  for (const field of STAR_FIELDS) {
    if (payload[field]) payload[field] = Math.round(Number(payload[field]));
    // 0 is this form's "not yet chosen" sentinel. The API validates 1-5 and would
    // reject it, so it must be omitted rather than sent.
    if (!payload[field]) delete payload[field];
  }

  return payload;
};

// Mirrors reviewService.SUBRATING_MAX_DIVERGENCE. The server rejects the payload on
// every draft save as well as on submit, so without this check a diverging pair
// silently blocks all saving until the user happens to change it.
const SUBRATING_MAX_DIVERGENCE = 3;
const SUBRATING_LABELS = {
  hotelRating: "Hotel",
  transportRating: "Transport",
  foodRating: "Food",
};

// The server is still the source of truth; this only spares the user a round trip.
const getRatingCoherenceIssues = (values) => {
  const issues = [];
  const overall = Number(values?.overallRating);
  // Unset ratings are dropped from the payload, so they can't diverge.
  if (!Number.isInteger(overall) || overall < 1) return issues;

  for (const field of Object.keys(SUBRATING_LABELS)) {
    const sub = Number(values?.[field]);
    if (!Number.isInteger(sub) || sub < 1) continue;
    if (Math.abs(sub - overall) > SUBRATING_MAX_DIVERGENCE) {
      issues.push({
        field,
        message: `${SUBRATING_LABELS[field]} (${sub}★) is too far from your overall rating (${overall}★). They can differ by at most ${SUBRATING_MAX_DIVERGENCE}.`,
      });
    }
  }
  return issues;
};

// Bounded backoff for transient auto-save failures. Deliberately short and finite —
// after the last attempt we stop and leave the user a manual Retry.
const AUTOSAVE_RETRY_DELAYS_MS = [3000, 8000, 20000];

// Maps a failure to what the user should see and whether retrying can help.
// Validation and expiry are permanent for the current payload; rate limits,
// server faults and network drops are worth another attempt.
const describeSaveError = (err) => {
  if (err?.status === 409 && err?.code === "DRAFT_EXPIRED") {
    return { message: "This draft has expired and can no longer be saved.", retryable: false };
  }
  if (err?.status === 429) {
    return { message: "Saving paused — too many changes at once. Retrying shortly…", retryable: true };
  }
  if (err?.status === 400 && err?.code === "PROFANITY") {
    // The server names the exact word it objected to. Repeat it, because the
    // draft stops saving until it is gone and a vague message leaves the writer
    // re-reading their own paragraph with no idea what to change.
    const offending = (err.issues || []).find((i) => i?.match)?.match;
    return {
      message: offending
        ? `The word "${offending}" isn't allowed. Remove it to keep saving.`
        : "Some of that wording isn't allowed. Edit it to keep saving.",
      retryable: false,
    };
  }
  if (err?.status === 400) {
    return { message: "Some fields need attention before this can be saved.", retryable: false };
  }
  if (err?.status === 403 || err?.status === 404) {
    return { message: "This review is no longer available to edit.", retryable: false };
  }
  if (err?.status === 401) {
    return { message: "Your session expired. Sign in again to keep saving.", retryable: false };
  }
  if (!err?.status) {
    // Drafts are server-side only since Round 12 — there is no longer a local copy to
    // promise the user, so this must not claim their changes are safe on this device.
    return { message: "Couldn't reach the server. Retrying…", retryable: true };
  }
  return { message: "Couldn't save your draft. Retrying…", retryable: true };
};

// The category *values* now come from GET /api/media/limits — only their labels and
// display order are ours. The endpoint returns "cover" first; we keep it last so the
// upload dropdown still opens on "destination", which is the staging default.
const CATEGORY_LABELS = {
  destination: "Destination",
  hotel: "Hotel",
  food: "Food",
  attraction: "Attraction",
  cover: "Cover",
  travel: "Travel Video",
};

const CATEGORY_DISPLAY_ORDER = ["destination", "hotel", "food", "attraction", "cover"];

const categoryLabel = (value) =>
  CATEGORY_LABELS[value] || String(value).charAt(0).toUpperCase() + String(value).slice(1);

const orderCategories = (categories = []) =>
  [...categories].sort(
    (a, b) =>
      (CATEGORY_DISPLAY_ORDER.indexOf(a) + 1 || 99) - (CATEGORY_DISPLAY_ORDER.indexOf(b) + 1 || 99),
  );

// Trip dates for the preview header. Returns null on anything unparseable so the
// caller omits the line rather than rendering "Invalid Date".
const formatTripDates = (start, end) => {
  if (!start || !end) return null;
  const from = new Date(start);
  const to = new Date(end);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;
  const sameYear = from.getFullYear() === to.getFullYear();
  const nights = Math.max(0, Math.round((to - from) / 86400000));
  const range = `${from.toLocaleDateString(undefined, { day: "numeric", month: "short", ...(sameYear ? {} : { year: "numeric" }) })} – ${to.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}`;
  return { range, nights };
};

// Read-only star row for the preview, where the stars report a score rather than
// collecting one — hence a span, not the interactive CompactStarRating buttons.
function StarDisplay({ value, size = "sm" }) {
  const px = size === "lg" ? "text-lg" : "text-sm";
  return (
    <span className={`inline-flex ${px} leading-none tracking-tight`} aria-label={`${value} out of 5`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <span key={star} className={star <= value ? "text-amber-400" : "text-gray-200"}>
          ★
        </span>
      ))}
    </span>
  );
}

const formatBytes = (bytes) => {
  if (!Number.isFinite(bytes)) return "";
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${Math.round(mb)}MB` : `${Math.round(bytes / 1024)}KB`;
};

// `StarRating` (the centred 4xl variant) and `RangeSlider` were removed here: every
// rating is now a `StarField` in a card, so nothing referenced either one.

// A labelled row of choices. Used for the enum dropdowns' values and for the
// aiHelpfulness scale, so a question reads as a sentence with answers under it
// rather than a select the user has to open.
function PillGroup({ options, value, onChange, disabled, tone = "teal" }) {
  const active = {
    teal: "bg-teal-600 border-teal-600 text-white shadow-sm",
    indigo: "bg-indigo-600 border-indigo-600 text-white shadow-sm",
  }[tone];

  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const selected = value === opt.value;
        return (
          <button
            key={String(opt.value)}
            type="button"
            disabled={disabled}
            aria-pressed={selected}
            // Re-clicking the current answer clears it: every one of these is
            // optional, and without this there is no way back to "unanswered".
            onClick={() => onChange(selected ? null : opt.value)}
            className={`px-4 py-2 rounded-full border text-sm font-semibold transition-all ${
              selected ? active : "bg-white border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50"
            } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// The same choice control as PillGroup, drawn as face tiles. Used where the answers
// sit on a clear good→bad axis and the face reads faster than the word.
function FacePillGroup({ options, value, onChange, disabled }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {options.map((opt) => {
        const selected = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            disabled={disabled}
            aria-pressed={selected}
            onClick={() => onChange(selected ? null : opt.value)}
            className={`flex flex-col items-center justify-center gap-2 py-4 px-2 rounded-xl border transition-all ${
              selected
                ? "bg-teal-50 border-teal-600 text-teal-700 shadow-sm"
                : "bg-white border-gray-200 text-gray-400 hover:border-gray-300 hover:text-gray-500"
            } ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
          >
            <opt.Icon className="w-6 h-6" />
            <span className="text-[11px] font-bold uppercase tracking-wide">{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// Prompt chips for the favourite-part field. Clicking one appends a heading the
// user then writes under — it seeds the answer rather than replacing what they typed.
const STORY_PROMPTS = ["Local hidden gems", "Hotel & stays", "Culinary tour", "Tour guides"];

const INSPIRATION_PROMPTS = [
  { Icon: Star, text: "What was the highlight of your stay?" },
  { Icon: Car, text: "How easy was the transport?" },
  { Icon: Compass, text: "Any tips for future travellers?" },
];

// Where each credit rule is earned and what to call the action. The codes are fixed
// server-side constants (reviewService.REWARD_PREVIEW_CODES) — the labels and the
// amounts still come from the API, so no editable number is duplicated here.
// `label` is a fallback only: when a rule is missing/inactive in the DB the preview
// endpoint echoes the raw code as the label, and "REVIEW_WITH_PHOTOS" is not
// something a traveller should ever read.
const REWARD_ACTIONS = {
  REVIEW_SUBMITTED: { step: 1, cta: "Rate your trip", label: "Submit your trip review" },
  REVIEW_DETAILED_BONUS: { step: 2, cta: "Write the details", label: "Write a detailed review" },
  REVIEW_WITH_PHOTOS: { step: 3, cta: "Add a photo", label: "Add photos to your review" },
  POST_PUBLISHED: { step: 2, cta: "Finish your review", label: "Publish to the community" },
};

// Rules the server cannot grant at submit time:
//   POST_PUBLISHED     — `post.published` is emitted from approvePost, so it lands
//                        when an admin approves the post.
//   REVIEW_WITH_PHOTOS — requires an *approved* photo, and uploads are created
//                        `pending` until a moderator clears them.
// Everything else is paid the moment the review is submitted.
const PAYS_ON_APPROVAL = new Set(["POST_PUBLISHED", "REVIEW_WITH_PHOTOS"]);

// The API's fallback label is the code itself — swap it for our human wording.
const rewardLabel = (item) =>
  item.label && item.label !== item.code ? item.label : REWARD_ACTIONS[item.code]?.label || item.label;

// Confetti burst fired when a step's fields are completed. Pure CSS particles —
// no emoji, no library. Geometry is derived from the particle index, so the burst
// is deterministic; the spread comes from mixing three radii and five colours.
const BURST_COLORS = ["#f59e0b", "#14b8a6", "#ef4444", "#fde047", "#818cf8"];
const BURST_PARTICLES = Array.from({ length: 16 }, (_, i) => {
  const angle = (i / 16) * Math.PI * 2;
  const dist = 90 + (i % 3) * 45;
  return {
    id: i,
    dx: Math.round(Math.cos(angle) * dist),
    dy: Math.round(Math.sin(angle) * dist),
    rot: (i % 2 === 0 ? 1 : -1) * (180 + i * 20),
    color: BURST_COLORS[i % BURST_COLORS.length],
    round: i % 3 === 0,
    delay: (i % 4) * 40,
  };
});

function CelebrationBurst({ burstKey, onDone }) {
  // Unmount after the longest particle (delay + flight) has finished.
  useEffect(() => {
    const t = setTimeout(onDone, 1000);
    return () => clearTimeout(t);
  }, [burstKey, onDone]);

  return (
    <div
      key={burstKey}
      // Above the success dialog (z-65): the burst fires as that dialog appears,
      // and behind it the particles would be hidden by the card itself.
      className="fixed inset-0 z-[70] pointer-events-none flex items-center justify-center"
      aria-hidden="true"
    >
      {BURST_PARTICLES.map((p) => (
        <span
          key={p.id}
          className="burst-particle"
          style={{
            "--dx": `${p.dx}px`,
            "--dy": `${p.dy}px`,
            "--rot": `${p.rot}deg`,
            width: p.round ? "9px" : "11px",
            height: p.round ? "9px" : "7px",
            borderRadius: p.round ? "50%" : "2px",
            background: p.color,
            animationDelay: `${p.delay}ms`,
          }}
        />
      ))}
    </div>
  );
}

// Coins flying from the Continue button into the rewards purse. Coordinates are
// measured at click time (the purse scrolls, the button is fixed), passed in as a
// `flight` and turned into per-coin CSS deltas.
const COIN_COUNT = 7;
const COIN_SIZE = 18;

function CoinFlight({ flight, onDone }) {
  useEffect(() => {
    // Longest coin = last delay + flight duration.
    const t = setTimeout(onDone, 1200);
    return () => clearTimeout(t);
  }, [flight, onDone]);

  return (
    <div className="fixed inset-0 z-[60] pointer-events-none" aria-hidden="true">
      {/* The amount rides along with the coins, so the deposit is readable and not
          just a shower of gold. */}
      {flight.amount > 0 && (
        <span
          className="coin-fly absolute px-2 py-0.5 rounded-full bg-amber-400 text-teal-900 text-xs font-bold shadow-sm whitespace-nowrap"
          style={{
            left: flight.fromX,
            top: flight.fromY - 26,
            "--tx": `${flight.toX - flight.fromX}px`,
            "--ty": `${flight.toY - flight.fromY}px`,
          }}
        >
          +{flight.amount}
        </span>
      )}

      {Array.from({ length: COIN_COUNT }, (_, i) => {
        // Fan the launch points out so the coins read as a handful, not a stack.
        const spread = (i - (COIN_COUNT - 1) / 2) * 15;
        const startX = flight.fromX + spread;
        return (
          <span
            key={i}
            className="coin-fly absolute"
            style={{
              left: startX - COIN_SIZE / 2,
              top: flight.fromY - COIN_SIZE / 2,
              "--tx": `${flight.toX - startX}px`,
              "--ty": `${flight.toY - flight.fromY}px`,
              animationDelay: `${i * 55}ms`,
            }}
          >
            <svg width={COIN_SIZE} height={COIN_SIZE} viewBox="0 0 20 20">
              <circle cx="10" cy="10" r="9" fill="#fde047" stroke="#f59e0b" strokeWidth="2" />
              <circle cx="10" cy="10" r="4.5" fill="none" stroke="#f59e0b" strokeWidth="1.5" />
            </svg>
          </span>
        );
      })}
    </div>
  );
}

// Animated gift for the purse card — a bobbing gift box, a coin floating out and
// twinkling sparkles. Inline SVG + CSS keyframes (index.css) so it ships in the
// bundle with no GIF/Lottie asset, and every loop obeys prefers-reduced-motion.
// The static frame (reduced motion) still reads as "gift + coin", not a blank.
function AnimatedRewardGift({ className = "w-16 h-16" }) {
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden="true">
      {/* Sparkles, staggered so they never blink in unison. */}
      {[
        { d: "M11 14 l1.6 4 4 1.6 -4 1.6 -1.6 4 -1.6 -4 -4 -1.6 4 -1.6 Z", delay: "0s" },
        { d: "M51 22 l1.3 3.2 3.2 1.3 -3.2 1.3 -1.3 3.2 -1.3 -3.2 -3.2 -1.3 3.2 -1.3 Z", delay: "0.7s" },
        { d: "M46 6 l1 2.6 2.6 1 -2.6 1 -1 2.6 -1 -2.6 -2.6 -1 2.6 -1 Z", delay: "1.3s" },
      ].map((s) => (
        <path key={s.d} d={s.d} fill="#fcd34d" className="gift-sparkle" style={{ animationDelay: s.delay }} />
      ))}

      {/* Coin escaping the box — drawn already airborne so the reduced-motion
          static frame looks intentional. */}
      <g className="gift-coin">
        <circle cx="32" cy="14" r="6" fill="#fde047" stroke="#f59e0b" strokeWidth="1.5" />
        <circle cx="32" cy="14" r="3.2" fill="none" stroke="#f59e0b" strokeWidth="1" />
      </g>

      {/* The gift itself bobs as one unit. */}
      <g className="gift-bob">
        <rect x="18" y="32" width="28" height="22" rx="3" fill="#fbbf24" />
        <rect x="18" y="32" width="28" height="5" fill="#f59e0b" opacity="0.55" />
        <rect x="15" y="25" width="34" height="9" rx="2" fill="#f59e0b" />
        <rect x="29.5" y="25" width="5" height="29" fill="#ef4444" />
        {/* Bow */}
        <circle cx="27" cy="22.5" r="3.6" fill="#ef4444" />
        <circle cx="37" cy="22.5" r="3.6" fill="#ef4444" />
        <circle cx="32" cy="23.5" r="2.2" fill="#dc2626" />
      </g>
    </svg>
  );
}

// A star field with its own question label, matching the card layout.
function StarField({ label, name, control, disabled, issue }) {
  return (
    <div>
      <p className="text-sm font-medium text-teal-700 mb-3">{label}</p>
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <CompactStarRating rating={field.value} setRating={field.onChange} disabled={disabled} size="lg" />
        )}
      />
      {issue && <p className="mt-2 text-xs text-red-600">{issue}</p>}
    </div>
  );
}

// Same control as StarRating at a size that sits inline next to a question label.
function CompactStarRating({ rating, setRating, disabled, size = "md" }) {
  return (
    <div className={`flex shrink-0 ${size === "lg" ? "gap-2" : "gap-1"}`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={disabled}
          aria-label={`${star} star${star > 1 ? "s" : ""}`}
          // Re-clicking the current value clears it — these ratings are optional
          // and 0 is how the payload builder recognises "unanswered".
          onClick={() => setRating(rating === star ? 0 : star)}
          className={`${size === "lg" ? "text-3xl" : "text-2xl"} leading-none focus:outline-none transition-transform ${
            star <= rating ? "text-yellow-400" : "text-gray-200 hover:text-yellow-200"
          } ${disabled ? "cursor-not-allowed opacity-70" : "cursor-pointer hover:scale-110"}`}
        >
          ★
        </button>
      ))}
    </div>
  );
}

export default function WriteReview() {
  const { tripId } = useParams();
  const navigate = useNavigate();
  // Only for greeting the traveller by name on the thank-you screen.
  const { user } = useAuth();
  
  // Base State
  const [currentStep, setCurrentStep] = useState(1);
  const [trip, setTrip] = useState(null);
  // Moderation outcome of the community post this review became — null until
  // a submitted review's post has been decided one way or the other.
  const [post, setPost] = useState(null);
  // The review id lives only in reviewIdRef — nothing renders from it, and a second
  // copy in state is what let callers read a stale null right after it was created.
  const [loading, setLoading] = useState(true);
  // "idle" | "saving" | "saved" | "error" — drives the visible save indicator.
  const [saveStatus, setSaveStatus] = useState("idle");
  const [saveErrorMessage, setSaveErrorMessage] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [status, setStatus] = useState("draft");
  const [showExpiredDialog, setShowExpiredDialog] = useState(false);
  // Reasons publishing can't proceed — shown instead of silently locking the review.
  const [publishBlockers, setPublishBlockers] = useState([]);
  const [reviewLockedUnpublished, setReviewLockedUnpublished] = useState(false);
  const [isRetryingPublish, setIsRetryingPublish] = useState(false);
  
  // Media State
  const [mediaList, setMediaList] = useState([]);
  const [stagingFiles, setStagingFiles] = useState([]);
  // Mirrors stagingFiles for the unmount-cleanup effect below — an effect
  // with an empty dep array closes over the state from its first render, so
  // reading stagingFiles directly there would always revoke an empty (or
  // stale) list instead of whatever was actually staged when the page unmounts.
  const stagingFilesRef = useRef(stagingFiles);
  useEffect(() => { stagingFilesRef.current = stagingFiles; }, [stagingFiles]);
  useEffect(() => {
    return () => {
      stagingFilesRef.current.forEach((f) => URL.revokeObjectURL(f.preview));
    };
  }, []);
  const [isUploading, setIsUploading] = useState(false);
  const [settingCoverId, setSettingCoverId] = useState(null);
  const fileInputRef = useRef(null);
  // A second, video-only picker so "Upload video" doesn't open a chooser full of
  // images the user then has to filter past.
  const videoInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);

  // Server-owned upload limits. Starts at the fallback so the form is usable before
  // the request resolves, and stays there if it fails.
  const [limits, setLimits] = useState(FALLBACK_MEDIA_LIMITS);

  // Rewards purse. Amounts are admin-editable at runtime, so nothing here is derived
  // from a hard-coded number — the whole breakdown is rendered from the response.
  const [rewards, setRewards] = useState(null);

  // Real community numbers for the "your review helps other people" nudge. The card
  // is hidden entirely if this fails rather than showing an invented figure.
  const [communityStats, setCommunityStats] = useState(null);

  // Auto-save bookkeeping.
  // savedSnapshot: serialized payload of the last successful save — the dirty check.
  // inFlight: the running save promise; two concurrent creates would make two reviews.
  // pendingAutoSave: an edit arrived mid-save and still needs writing.
  // reviewId is mirrored into a ref so saveDraft reads it without waiting for a render.
  const savedSnapshotRef = useRef(null);
  const inFlightRef = useRef(null);
  const pendingAutoSaveRef = useRef(false);
  const autoSaveTimerRef = useRef(null);
  const reviewIdRef = useRef(null);
  const saveDraftRef = useRef(null);
  const googleWindowRef = useRef(null);
  // Endpoints for the coin flight: the Continue button, the purse card at the top of
  // the page, and the sticky bar's credit readout as the fallback destination when
  // the purse has been scrolled out of view.
  const nextButtonRef = useRef(null);
  const purseRef = useRef(null);
  const barCreditsRef = useRef(null);
  // How many consecutive auto-retries we've burned on the current failure.
  const retryAttemptRef = useRef(0);

  const { register, handleSubmit, control, watch, reset, setError, clearErrors, setValue, getValues, formState: { errors } } = useForm({
    defaultValues: {
      overallRating: 0,
      description: "",
      whatWentWrong: "",
      bestExperience: "",
      improvements: "",
      hotelRating: 0,
      transportRating: 0,
      foodRating: 0,
      aiHelpfulness: 0,
      itinerarySatisfaction: 0,
      valueForMoney: 0,
      itineraryEasyToFollow: 0,
      dealPerception: null,
      vegetarianOptions: null,
      itineraryCoverage: null,
      itineraryPace: null,
      wouldRecommend: null
    }
  });

  const overallRating = watch("overallRating");
  const descriptionContent = watch("description");
  // `whatWentWrong` is only asked for when the trip fell short — mirrors the spec's
  // "show only when overallRating < 4".
  const showWhatWentWrong = Number(overallRating) > 0 && Number(overallRating) < 4;

  // Mirrors the server's REVIEW_DETAILED_BONUS rule in events/index.js: a trimmed
  // description of at least 200 chars AND these four specific ratings filled. Note it
  // is hotelRating — not transport or food — that counts toward the bonus.
  const DETAILED_DESCRIPTION_MIN = 200;
  const detailedRatingFields = ["hotelRating", "itineraryEasyToFollow", "itinerarySatisfaction", "valueForMoney"];
  const detailedDescriptionLen = (descriptionContent || "").trim().length;
  const missingDetailedRatings = detailedRatingFields.filter((f) => !watch(f));
  const detailedBonusUnlocked =
    detailedDescriptionLen >= DETAILED_DESCRIPTION_MIN && missingDetailedRatings.length === 0;

  const stepProgress = Math.round((currentStep / TOTAL_STEPS) * 100);

  // Preview-only derivations. `previewRatings` keeps only the categories actually
  // scored, so the published card never implies a rating the user didn't give.
  const tripDates = formatTripDates(trip?.startDate, trip?.endDate);
  const previewRatings = [
    { name: "itinerarySatisfaction", label: "Itinerary", Icon: Map },
    { name: "hotelRating", label: "Hotel", Icon: Building },
    { name: "transportRating", label: "Transport", Icon: Car },
    { name: "foodRating", label: "Food", Icon: Utensils },
    { name: "valueForMoney", label: "Value for money", Icon: Gift },
    { name: "itineraryEasyToFollow", label: "Easy to follow", Icon: Check },
    { name: "aiHelpfulness", label: "Romaya's help", Icon: Sparkles },
  ].filter((r) => watch(r.name));

  // Which rewards the form, as it stands right now, would earn on submit. This is a
  // client-side mirror of conditions the server checks at grant time — amounts and
  // the `unlocked` truth still come from the API. It exists so the purse can climb
  // live as the user works, instead of showing one static maximum the whole way.
  // `!= null` catches undefined too — a hydrated draft whose API payload omits
  // wouldRecommend would otherwise read as answered and overstate the purse.
  const submitFieldsReady =
    !!overallRating &&
    !!watch("aiHelpfulness") &&
    watch("wouldRecommend") != null &&
    detailedDescriptionLen > 0;
  // Rejected media earns nothing server-side, so it must not count here either.
  const countableMedia = mediaList.filter((m) => m.status !== "rejected");
  const hasCountablePhoto = countableMedia.some((m) => m.kind === "photo");

  const rewardOnTrack = {
    REVIEW_SUBMITTED: submitFieldsReady,
    REVIEW_WITH_PHOTOS: hasCountablePhoto,
    REVIEW_DETAILED_BONUS: detailedBonusUnlocked,
    // The publish bonus needs the review AND at least one photo or video — the
    // server skips the award for a post with no media.
    POST_PUBLISHED: submitFieldsReady && countableMedia.length > 0,
  };
  // What each unearned reward is still waiting on, and where to go and do it.
  // The server's SUBMIT_REQUIRED is the authority for REVIEW_SUBMITTED — the
  // written review counts toward it, so this must never suggest that finishing
  // the step 1 ratings alone earns those credits.
  const DETAILED_RATING_STEPS = {
    hotelRating: 1,
    itinerarySatisfaction: 1,
    itineraryEasyToFollow: 1,
    valueForMoney: 2,
  };
  const rewardGap = (code) => {
    if (code === "REVIEW_SUBMITTED") {
      const missing = [];
      if (!overallRating) missing.push({ text: "an overall rating", step: 1 });
      if (!watch("aiHelpfulness")) missing.push({ text: "how helpful Romaya was", step: 1 });
      if (watch("wouldRecommend") == null) missing.push({ text: "your recommendation", step: 1 });
      if (detailedDescriptionLen === 0) missing.push({ text: "your written review", step: 2 });
      if (missing.length === 0) return null;
      return {
        text: `Still needs ${missing.map((m) => m.text).join(", ")}.`,
        step: missing[0].step,
        cta: missing[0].step === 2 ? "Write your review" : "Rate your trip",
      };
    }
    if (code === "REVIEW_DETAILED_BONUS") {
      if (detailedDescriptionLen < DETAILED_DESCRIPTION_MIN) {
        return {
          text: `${DETAILED_DESCRIPTION_MIN - detailedDescriptionLen} more characters in your review.`,
          step: 2,
          cta: "Write the details",
        };
      }
      if (missingDetailedRatings.length > 0) {
        return {
          text: `Rate ${missingDetailedRatings.length} more ${missingDetailedRatings.length === 1 ? "category" : "categories"}.`,
          step: DETAILED_RATING_STEPS[missingDetailedRatings[0]] || 1,
          cta: "Finish the ratings",
        };
      }
      return null;
    }
    if (code === "REVIEW_WITH_PHOTOS") {
      return { text: "Upload at least one photo from the trip.", step: 3, cta: "Add a photo" };
    }
    if (code === "POST_PUBLISHED") {
      // Media is the part travellers forget; the review fields are covered by
      // their own row above, so lead with whichever is actually missing.
      if (countableMedia.length === 0) {
        return { text: "Add at least one photo or video.", step: 3, cta: "Add a photo" };
      }
      return rewardGap("REVIEW_SUBMITTED");
    }
    return null;
  };

  const rewardBreakdown = rewards?.breakdown || [];
  const securedCredits = rewardBreakdown
    .filter((b) => b.unlocked || rewardOnTrack[b.code])
    .reduce((sum, b) => sum + (b.amount || 0), 0);
  // Secured but not yet granted, split by when the server actually pays it.
  // POST_PUBLISHED is awarded on `post.published`, which now fires when an admin
  // approves the post — so it must not be counted into what Submit pays today.
  const securedUngranted = rewardBreakdown.filter((b) => !b.unlocked && rewardOnTrack[b.code]);
  const claimableCredits = securedUngranted
    .filter((b) => !PAYS_ON_APPROVAL.has(b.code))
    .reduce((sum, b) => sum + (b.amount || 0), 0);
  const approvalCredits = securedUngranted
    .filter((b) => PAYS_ON_APPROVAL.has(b.code))
    .reduce((sum, b) => sum + (b.amount || 0), 0);
  // What's still on the table — drives the "don't leave credits behind" nudges.
  const missedRewards = rewardBreakdown.filter((b) => !b.unlocked && !rewardOnTrack[b.code]);
  const missedCredits = missedRewards.reduce((sum, b) => sum + (b.amount || 0), 0);

  // The nearest reward still on the table, in the API's own order — which runs in
  // the order the form is filled, so the first hit is the next thing to do. The
  // purse shows this so a wallet sitting at 0 says why, instead of looking broken.
  const nextRewardItem = missedRewards.find((b) => b.amount > 0) || null;
  const nextRewardHint = nextRewardItem
    ? { amount: nextRewardItem.amount, ...rewardGap(nextRewardItem.code) }
    : null;

  // Celebrate the moment a reward flips to "on track" — once per flip, never on
  // load (a hydrated draft starts with its rewards already earned/on-track).
  const prevOnTrackRef = useRef(null);
  useEffect(() => {
    // While the draft is hydrating, form values flip from empty to loaded — that
    // must set the baseline, not fire a celebration for work done last session.
    if (loading) {
      prevOnTrackRef.current = null;
      return;
    }
    if (status !== "draft") return;
    const prev = prevOnTrackRef.current;
    prevOnTrackRef.current = { ...rewardOnTrack };
    if (!prev || rewardBreakdown.length === 0) return;
    for (const b of rewardBreakdown) {
      if (!b.unlocked && rewardOnTrack[b.code] && !prev[b.code] && b.amount > 0) {
        toast.success(`+${b.amount} ${rewards?.currency || "RoamPoints"} secured — ${rewardLabel(b)}`, {
          toastId: `reward-secured-${b.code}`,
          icon: <Gift className="w-5 h-5 text-teal-600" />,
        });
      }
    }
    // Serialized deps: the object is rebuilt every render, so compare by value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, status, JSON.stringify(rewardOnTrack), rewardBreakdown.length]);

  // Confetti when a step is completed. Mirrors stepsMeta's `done` conditions —
  // fires on the flip from unfinished to finished, never on hydrating a draft
  // that already had the step done.
  const [burst, setBurst] = useState(0);
  const clearBurst = useCallback(() => setBurst(0), []);
  const [flight, setFlight] = useState(null);
  const clearFlight = useCallback(() => setFlight(null), []);
  // Set once the review is submitted AND published — drives the success dialog.

  // Sends coins from the Continue button up to the purse. Every endpoint has a
  // viewport-derived fallback: an element can be absent (unmounted) or zero-sized
  // (`hidden` at this breakpoint), and silently skipping the animation in those
  // cases is indistinguishable from the feature being broken.
  const launchCoinFlight = useCallback((amount) => {
    const usable = (el) => {
      const rect = el?.getBoundingClientRect();
      return rect && rect.width > 0 && rect.height > 0 ? rect : null;
    };
    const centreOf = (rect) => ({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });

    const button = usable(nextButtonRef.current);
    const purse = usable(purseRef.current);
    const barChip = usable(barCreditsRef.current);

    // Source: the Continue button, else the bottom centre of the viewport.
    const from = button
      ? centreOf(button)
      : { x: window.innerWidth / 2, y: window.innerHeight - 48 };

    // Target: the purse while it is still on screen, else the sticky bar's credit
    // figure, else the top-right corner where the purse lives.
    const targetRect = purse && purse.bottom > 8 ? purse : barChip;
    const to = targetRect
      ? centreOf(targetRect)
      : { x: window.innerWidth - 120, y: 96 };

    setFlight({ key: Date.now(), amount, fromX: from.x, fromY: from.y, toX: to.x, toY: to.y });
  }, []);

  // The wallet figure lags `securedCredits` on purpose: it holds until the coins
  // have crossed the screen, then counts up as they land, so the credits visibly
  // arrive rather than having already changed before the coins set off.
  const [displayedCredits, setDisplayedCredits] = useState(0);
  const [walletCatch, setWalletCatch] = useState(0);
  const displayedRef = useRef(0);
  const creditsSeededRef = useRef(false);

  useEffect(() => {
    if (loading || !rewards) return undefined;

    const start = displayedRef.current;

    // First reading after load, and any decrease (a deleted photo retracting a
    // bonus), land immediately — only growth is worth animating.
    if (!creditsSeededRef.current || securedCredits < start) {
      creditsSeededRef.current = true;
      displayedRef.current = securedCredits;
      setDisplayedCredits(securedCredits);
      return undefined;
    }
    if (securedCredits === start) return undefined;

    const delta = securedCredits - start;
    const COIN_TRAVEL_MS = 620;
    const COUNT_MS = 500;
    let frame;

    const landing = setTimeout(() => {
      setWalletCatch((n) => n + 1);
      let began;
      const tick = (now) => {
        if (began === undefined) began = now;
        const progress = Math.min(1, (now - began) / COUNT_MS);
        const value = Math.round(start + delta * progress);
        displayedRef.current = value;
        setDisplayedCredits(value);
        if (progress < 1) frame = requestAnimationFrame(tick);
      };
      frame = requestAnimationFrame(tick);
    }, COIN_TRAVEL_MS);

    return () => {
      clearTimeout(landing);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [loading, rewards, securedCredits]);

  // The denominator for every progress figure: the full amount the admin has
  // switched on for this review, not just what is on track right now.
  //
  // `potential` moves as the user types — it only counts rewards whose gate is
  // already met — so using it here made the target grow while the bar was being
  // filled ("0 / 100" beside a hint promising a +250 bonus). The ceiling holds
  // still, which is what a progress bar needs.
  //
  // Falls back to `potential` so a client running against an older API (no
  // `maxPotential` in the payload) still shows a sane bar rather than 0.
  const rewardCeiling = rewards?.maxPotential ?? rewards?.potential ?? 0;

  // How much of the purse is secured, as a percentage — drives the sidebar ring.
  // Reads the animated figure so the ring fills in step with the coins landing.
  // Declared here, after displayedCredits, not up with the other reward maths.
  const creditsProgress =
    rewardCeiling > 0 ? Math.round((displayedCredits / rewardCeiling) * 100) : 0;

  // Coins fly when the purse actually grows — that is the moment credits land, and
  // tying the animation to anything else (a Continue click that secured nothing)
  // shows money moving into a total that never changes.
  const prevSecuredRef = useRef(null);
  useEffect(() => {
    if (loading || !rewards) {
      prevSecuredRef.current = null;
      return;
    }
    const prev = prevSecuredRef.current;
    prevSecuredRef.current = securedCredits;
    if (prev === null || securedCredits <= prev) return;
    launchCoinFlight(securedCredits - prev);
  }, [loading, rewards, securedCredits, launchCoinFlight]);

  const stepDoneFlags = [
    !!overallRating && !!watch("aiHelpfulness") && watch("wouldRecommend") !== null,
    detailedDescriptionLen > 0,
    rewardOnTrack.POST_PUBLISHED,
  ];
  const prevStepDoneRef = useRef(null);
  useEffect(() => {
    if (loading) {
      prevStepDoneRef.current = null;
      return;
    }
    const prev = prevStepDoneRef.current;
    prevStepDoneRef.current = stepDoneFlags;
    if (!prev || status !== "draft") return;
    if (stepDoneFlags.some((done, i) => done && !prev[i])) {
      setBurst((b) => b + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, status, ...stepDoneFlags]);

  // Seeds the favourite-part field with a heading to write under. Appends rather
  // than replaces, so tapping a second prompt never destroys the first answer.
  const applyStoryPrompt = (prompt) => {
    // `isLocked` is derived below the early return; read status directly.
    if (status !== "draft") return;
    const current = getValues("bestExperience") || "";
    const prefix = current.trim().length > 0 ? `${current.replace(/\s+$/, "")}\n\n` : "";
    setValue("bestExperience", `${prefix}${prompt}: `, { shouldDirty: true });
  };

  // Only surfaced when the bonus is still reachable and not yet earned.
  const detailedBonus = (rewards?.breakdown || []).find(b => b.code === "REVIEW_DETAILED_BONUS");
  const photoBonus = (rewards?.breakdown || []).find(b => b.code === "REVIEW_WITH_PHOTOS");
  const bonusHint =
    // No amount configured means no bonus to promise — "+0 RoamPoints" reads as a bug.
    !detailedBonus || !(detailedBonus.amount > 0) || detailedBonus.unlocked || detailedBonusUnlocked
      ? null
      : detailedDescriptionLen < DETAILED_DESCRIPTION_MIN
        ? `Write ${DETAILED_DESCRIPTION_MIN - detailedDescriptionLen} more characters for +${detailedBonus.amount} bonus RoamPoints.`
        : `Rate ${missingDetailedRatings.length} more ${missingDetailedRatings.length === 1 ? "category" : "categories"} for +${detailedBonus.amount} bonus RoamPoints.`;

  // Live rating-coherence check so the slider shows the problem as it's created,
  // rather than surfacing as a server error at submit time.
  const ratingIssues = getRatingCoherenceIssues({
    overallRating,
    hotelRating: watch("hotelRating"),
    transportRating: watch("transportRating"),
    foodRating: watch("foodRating"),
  });
  const ratingIssueByField = Object.fromEntries(
    ratingIssues.map((issue) => [issue.field, issue.message]),
  );
  
  const fetchGallery = async () => {
    try {
      const mediaData = await getMediaGallery(tripId);
      const flatPhotos = Object.values(mediaData.photos || {}).flat();
      const flatVideos = Object.values(mediaData.videos || {}).flat();
      setMediaList([...flatPhotos, ...flatVideos]);
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "media");
        scope.setTag("app", "web");
        scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
        scope.setContext("review", { trip_id: tripId });
        Sentry.captureException(new MediaError(err instanceof Error ? err.message : String(err)));
      });
      console.error("Error fetching gallery:", err);
    }
  };

  // Purse amounts change as the user fills the form (photo bonus, detailed bonus), so
  // this is re-read after saves and uploads rather than fetched once.
  const refreshRewards = useCallback(async () => {
    try {
      setRewards(await getRewardsPreview(tripId));
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "payment");
        scope.setTag("app", "web");
        scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
        scope.setContext("review", { trip_id: tripId });
        Sentry.captureException(new PaymentError(err instanceof Error ? err.message : String(err)));
      });
      // The purse is a progress indicator, not a gate — a failure here must never
      // block writing or submitting a review.
      console.error("[WriteReview] Rewards preview failed:", err);
    }
  }, [tripId]);

  useEffect(() => {
    async function loadData() {
      try {
        const [tripData, reviewData] = await Promise.all([
          getTrip(tripId),
          getReviewForTrip(tripId),
          fetchGallery()
        ]);

        setTrip(tripData);

        if (reviewData && reviewData.review) {
          const rev = reviewData.review;
          reviewIdRef.current = rev.id;
          setStatus(rev.status);
          setPost(rev.post || null);
          const loadedValues = {
            overallRating: rev.overallRating || 0,
            description: rev.description || "",
            whatWentWrong: rev.whatWentWrong || "",
            bestExperience: rev.bestExperience || "",
            improvements: rev.improvements || "",
            hotelRating: rev.hotelRating || 3,
            transportRating: rev.transportRating || 3,
            foodRating: rev.foodRating || 3,
            aiHelpfulness: rev.aiHelpfulness || 0,
            itinerarySatisfaction: rev.itinerarySatisfaction || 0,
            valueForMoney: rev.valueForMoney || 0,
            itineraryEasyToFollow: rev.itineraryEasyToFollow || 0,
            dealPerception: rev.dealPerception ?? null,
            vegetarianOptions: rev.vegetarianOptions ?? null,
            itineraryCoverage: rev.itineraryCoverage ?? null,
            itineraryPace: rev.itineraryPace ?? null,
            wouldRecommend: rev.wouldRecommend ?? null
          };
          reset(loadedValues);
          // What we just loaded is what the server already has — start clean, so
          // simply opening a saved draft never triggers a write.
          savedSnapshotRef.current = JSON.stringify(buildDraftPayload(loadedValues));
        }
        // No server draft is no longer a special case: since Round 12 the API accepts
        // a draft with zero fields filled, so the first autosave creates it.
      } catch (err) {
        Sentry.withScope((scope) => {
          scope.setTag("feature", "community");
          scope.setTag("app", "web");
          scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
          Sentry.captureException(new CommunityError(err instanceof Error ? err.message : String(err)));
        });
        if (err.status === 429) {
          toast.error(`Slow down. Try again in ${err.retryAfter || 'a few'} seconds.`);
        } else {
          console.error("Error loading review data", err);
        }
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [tripId, reset]);

  // Limits are public and unauthenticated, and the form is usable on the fallback
  // while this is in flight — so it is deliberately not part of the blocking load.
  useEffect(() => {
    let cancelled = false;
    getMediaLimits()
      .then((data) => {
        if (!cancelled && data) setLimits({ ...FALLBACK_MEDIA_LIMITS, ...data });
      })
      .catch((err) => {
        Sentry.withScope((scope) => {
          scope.setTag("feature", "media");
          scope.setTag("app", "web");
          scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
          Sentry.captureException(new MediaError(err instanceof Error ? err.message : String(err)));
        });
        console.error("[WriteReview] Media limits fetch failed, using fallback:", err);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => { refreshRewards(); }, [refreshRewards]);

  useEffect(() => {
    let cancelled = false;
    getCommunityStats()
      .then((data) => { if (!cancelled) setCommunityStats(data); })
      .catch((err) => {
        Sentry.withScope((scope) => {
          scope.setTag("feature", "community");
          scope.setTag("app", "web");
          scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
          Sentry.captureException(new CommunityError(err instanceof Error ? err.message : String(err)));
        });
        // Purely motivational — never worth surfacing to the user.
        console.error("[WriteReview] Community stats fetch failed:", err);
      });
    return () => { cancelled = true; };
  }, []);

  const saveDraft = useCallback(async (isAutoSave = true) => {
    if (status !== "draft") return;

    if (!isAutoSave) {
      Sentry.addBreadcrumb({
        category: "user-action",
        message: "Saved review draft",
        level: "info",
      });
    }

    // One save at a time. Without this, two ticks can both see a null reviewId and
    // create two reviews for the same trip. Auto-saves defer; explicit saves (Next,
    // Submit) wait for the running one so they never act on a stale reviewId.
    if (inFlightRef.current) {
      if (isAutoSave) {
        pendingAutoSaveRef.current = true;
        return;
      }
      try {
        await inFlightRef.current;
      } catch (err) {
        Sentry.withScope((scope) => {
          scope.setTag("feature", "community");
          scope.setTag("app", "web");
          scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
          Sentry.captureException(new CommunityError(err instanceof Error ? err.message : String(err)));
        });
        // Already surfaced by the call that owns this request.
      }
    }

    const currentData = getValues();
    const payload = buildDraftPayload(currentData);

    // No rating gate here any more. The API accepts a draft with zero fields filled
    // since Round 12, so everything the user types is persisted server-side from the
    // first keystroke — which is what removed the need for a localStorage copy.

    // The server applies this rule to draft updates too, so sending a diverging
    // pair would 400 every time. Surface it here instead of burning the request.
    const coherenceIssues = getRatingCoherenceIssues(currentData);
    if (coherenceIssues.length > 0) {
      setSaveStatus("error");
      setSaveErrorMessage(coherenceIssues[0].message);
      return;
    }

    const snapshot = JSON.stringify(payload);

    // Nothing changed since the last successful save — don't spend a request.
    if (snapshot === savedSnapshotRef.current) return;

    const request = (async () => {
      if (!reviewIdRef.current) {
        // Send the full draft, not just the rating — everything the user has typed
        // is persisted by this one request, so closing the tab straight after the
        // first auto-save loses nothing.
        const created = await createReview(tripId, payload);
        reviewIdRef.current = created.review.id;
        return created;
      }
      return await updateReview(reviewIdRef.current, payload);
    })();

    inFlightRef.current = request;
    setSaveStatus("saving");

    try {
      const res = await request;
      // Only now is the draft clean; on failure the snapshot stays stale so the
      // next edit retries instead of silently dropping the change.
      savedSnapshotRef.current = snapshot;
      if (res && res.review && !isAutoSave) {
        reset(res.review);
        // Re-derive from getValues() so the snapshot always has the same basis as
        // the dirty check above, whatever extra fields the server echoed back.
        savedSnapshotRef.current = JSON.stringify(buildDraftPayload(getValues()));
      }
      // Timestamp only ever advances on a save the server accepted.
      setLastSaved(new Date());
      setSaveStatus("saved");
      setSaveErrorMessage(null);
      // The server just accepted this text, so any moderation error still shown
      // against it is stale — clear it the moment the offending word is gone.
      clearErrors(MODERATED_TEXT_FIELDS);
      toast.dismiss("review-autosave-error");
      retryAttemptRef.current = 0;
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "community");
        scope.setTag("app", "web");
        scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
        scope.setContext("review", { trip_id: tripId });
        Sentry.captureException(new CommunityError(err instanceof Error ? err.message : String(err)));
      });
      // Full detail stays in the console; the UI gets the plain-language version.
      console.error("[WriteReview] Draft save failed:", err);

      const { message, retryable } = describeSaveError(err);
      const attempt = retryAttemptRef.current;
      const canRetry = retryable && attempt < AUTOSAVE_RETRY_DELAYS_MS.length;

      setSaveStatus("error");
      setSaveErrorMessage(message);

      // One toast per failure episode — toastId dedupes so retries don't stack.
      toast.error(message, { toastId: "review-autosave-error" });

      if (err.status === 409 && err.code === "DRAFT_EXPIRED") {
        setShowExpiredDialog(true);
      } else if (err.status === 400 && err.issues) {
        // Moderation rejections are mapped even on an auto-save: typing is the
        // only way this error is ever reached, so skipping auto-saves would mean
        // the writer never sees which field to fix. Other 400s stay manual-only,
        // so half-typed fields aren't marked invalid mid-sentence.
        if (err.code === "PROFANITY" || !isAutoSave) {
          mapServerIssuesToForm(err.issues, setError);
        }
      }

      if (canRetry) {
        retryAttemptRef.current = attempt + 1;
        // 429 tells us how long to wait; otherwise use the backoff ladder.
        const delay =
          err.status === 429 && Number(err.retryAfter) > 0
            ? Number(err.retryAfter) * 1000
            : AUTOSAVE_RETRY_DELAYS_MS[attempt];
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = setTimeout(() => saveDraftRef.current?.(true), delay);
      }
    } finally {
      inFlightRef.current = null;

      // An edit landed while we were saving — re-arm the debounce so it isn't lost.
      if (pendingAutoSaveRef.current) {
        pendingAutoSaveRef.current = false;
        clearTimeout(autoSaveTimerRef.current);
        autoSaveTimerRef.current = setTimeout(
          () => saveDraftRef.current?.(true),
          AUTOSAVE_DEBOUNCE_MS,
        );
      }
    }
  }, [tripId, status, getValues, reset, setError, clearErrors]);

  // Read through a ref so the subscription below stays mounted across re-renders
  // and never tears down a debounce timer that is about to fire.
  useEffect(() => {
    saveDraftRef.current = saveDraft;
  });

  // Auto-save on edits only, debounced. Replaces the old fixed 5s interval, which
  // wrote ~720 times/hour from an idle tab and tripped the 30-writes/min limit.
  useEffect(() => {
    if (status !== "draft") return undefined;

    const subscription = watch(() => {
      // New content means a new attempt — give it the full retry budget rather
      // than inheriting the exhausted count from a previous failure.
      retryAttemptRef.current = 0;
      clearTimeout(autoSaveTimerRef.current);
      autoSaveTimerRef.current = setTimeout(
        () => saveDraftRef.current?.(true),
        AUTOSAVE_DEBOUNCE_MS,
      );
    });

    return () => {
      subscription.unsubscribe();
      clearTimeout(autoSaveTimerRef.current);
    };
  }, [watch, status]);

  const handleDeleteDraft = async () => {
    Sentry.addBreadcrumb({
      category: "user-action",
      message: "Deleted review draft",
      level: "info",
    });
    if (!reviewIdRef.current) return;
    try {
      await deleteReview(reviewIdRef.current);
      navigate("/dashboard");
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "community");
        scope.setTag("app", "web");
        scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
        scope.setContext("review", { trip_id: tripId });
        Sentry.captureException(new CommunityError(err instanceof Error ? err.message : String(err)));
      });
      toast.error("Failed to delete draft.");
    }
  };

  // Media Handlers
  const uploadedPhotos = mediaList.filter(m => m.kind === "photo");
  const uploadedVideos = mediaList.filter(m => m.kind === "video");
  // Mirrors the server's publish pre-check: one non-deleted photo marked as cover.
  const coverPhoto = uploadedPhotos.find(m => m.category === "cover") || null;
  const hasCoverPhoto = !!coverPhoto;

  // Takes a plain file list so the picker, the video-only picker and drag-and-drop
  // all go through exactly the same validation.
  const stageFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    let newStaging = [...stagingFiles];
    let addedPhotos = 0;
    let addedVideos = 0;

    for (const file of files) {
      const isVideo = file.type.startsWith("video/");
      const kind = isVideo ? "video" : "photo";

      const totalPhotos = uploadedPhotos.length + newStaging.filter(f => f.kind === "photo").length;
      const totalVideos = uploadedVideos.length + newStaging.filter(f => f.kind === "video").length;

      if (kind === "photo" && totalPhotos + addedPhotos >= limits.photosPerTrip) {
        toast.error(`Max ${limits.photosPerTrip} photos allowed.`); continue;
      }
      if (kind === "video" && totalVideos + addedVideos >= limits.videosPerTrip) {
        toast.error(`Max ${limits.videosPerTrip} videos allowed.`); continue;
      }
      if (kind === "photo" && file.size > limits.maxPhotoBytes) {
        toast.error(`File ${file.name} is too large. Max ${formatBytes(limits.maxPhotoBytes)}.`); continue;
      }
      if (kind === "video" && file.size > limits.maxVideoBytes) {
        toast.error(`File ${file.name} is too large. Max ${formatBytes(limits.maxVideoBytes)}.`); continue;
      }

      newStaging.push({
        id: crypto.randomUUID(),
        file,
        kind,
        category: kind === "video" ? "travel" : "destination",
        preview: URL.createObjectURL(file),
        status: "pending",
        progress: 0,
        error: null
      });

      if (kind === "photo") addedPhotos++;
      if (kind === "video") addedVideos++;
    }

    setStagingFiles(newStaging);
    // Reset both pickers so re-selecting the same file still fires a change event.
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (videoInputRef.current) videoInputRef.current.value = "";
  };

  const handleFileSelect = (e) => stageFiles(e.target.files);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (status !== "draft") return;
    stageFiles(e.dataTransfer?.files);
  };

  // Returns the batch outcome so callers (Continue) can decide whether to move on.
  const startUpload = async () => {
    if (isUploading) return { uploaded: 0, failed: 0 };
    setIsUploading(true);
    let batchHadSuccess = false;
    let uploaded = 0;
    let failed = 0;

    for (let i = 0; i < stagingFiles.length; i++) {
      const sf = stagingFiles[i];
      if (sf.status === "success") continue;

      const updateStaging = (updates) => {
        setStagingFiles(prev => prev.map(f => f.id === sf.id ? { ...f, ...updates } : f));
      };

      try {
        updateStaging({ status: "uploading", progress: 0, error: null });
        const payload = { kind: sf.kind, category: sf.category, contentType: sf.file.type, sizeBytes: sf.file.size };
        const { media, upload } = await presignMediaUpload(tripId, payload);

        await uploadFileToS3WithProgress(upload.uploadUrl, sf.file, sf.file.type, (prog) => updateStaging({ progress: prog }));
        updateStaging({ status: "finalizing", progress: 100 });
        await finalizeMediaUpload(media.id);

        updateStaging({ status: "success" });
        batchHadSuccess = true;
        uploaded++;
      } catch (err) {
        Sentry.withScope((scope) => {
          scope.setTag("feature", "media");
          scope.setTag("app", "web");
          scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
          Sentry.captureException(new MediaError(err instanceof Error ? err.message : String(err)));
        });
        updateStaging({ status: "error", error: err.message || "Upload failed" });
        failed++;
      }
    }

    setIsUploading(false);
    if (batchHadSuccess) {
      await fetchGallery();
      // A first approved photo unlocks REVIEW_WITH_PHOTOS.
      refreshRewards();
      setTimeout(fetchGallery, 3000);
      setTimeout(() => setStagingFiles(prev => {
        prev.filter(f => f.status === "success").forEach(f => URL.revokeObjectURL(f.preview));
        return prev.filter(f => f.status !== "success");
      }), 1000);
    }
    return { uploaded, failed };
  };

  const handleDeleteMedia = async (mediaId) => {
    if (!window.confirm("Delete this media?")) return;
    try {
      await deleteMedia(mediaId);
      setMediaList(prev => prev.filter(m => m.id !== mediaId));
      // Losing the last photo can retract the photo bonus.
      refreshRewards();
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "media");
        scope.setTag("app", "web");
        scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
        scope.setContext("review", { trip_id: tripId });
        Sentry.captureException(new MediaError(err instanceof Error ? err.message : String(err)));
      });
      toast.error("Failed to delete: " + err.message);
    }
  };

  // Captions are supported by PATCH /api/media/:id and were never wired up. They are
  // saved on blur rather than per keystroke — one request per edit, not per character.
  const handleCaptionSave = async (mediaId, caption) => {
    const existing = mediaList.find(m => m.id === mediaId);
    const next = caption.trim();
    if (!existing || (existing.caption || "") === next) return;
    if (next.length > CAPTION_MAX) {
      toast.error(`Captions are limited to ${CAPTION_MAX} characters.`);
      return;
    }
    // Optimistic: the field already shows the new text, so reverting on failure is
    // what keeps the UI honest about what was actually stored.
    setMediaList(prev => prev.map(m => (m.id === mediaId ? { ...m, caption: next } : m)));
    try {
      await updateMedia(mediaId, { caption: next || null });
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "media");
        scope.setTag("app", "web");
        scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
        scope.setContext("review", { trip_id: tripId });
        Sentry.captureException(new MediaError(err instanceof Error ? err.message : String(err)));
      });
      setMediaList(prev => prev.map(m => (m.id === mediaId ? { ...m, caption: existing.caption } : m)));
      toast.error("Couldn't save that caption: " + err.message);
    }
  };

  // Swapping the cover is one PATCH. The server demotes the existing cover to
  // "destination" inside the same transaction, so there is no delete-and-reupload
  // dance and no 409 to handle — re-running it on the current cover is a no-op.
  const handleSetCover = async (mediaId) => {
    // `isLocked` is derived further down the render; read `status` directly so this
    // handler does not depend on declaration order.
    if (settingCoverId || status !== "draft") return;
    setSettingCoverId(mediaId);
    try {
      await updateMedia(mediaId, { category: "cover" });
      await fetchGallery();
      toast.success("Cover photo updated.");
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "media");
        scope.setTag("app", "web");
        scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
        scope.setContext("review", { trip_id: tripId });
        Sentry.captureException(new MediaError(err instanceof Error ? err.message : String(err)));
      });
      toast.error("Couldn't set the cover photo: " + err.message);
    } finally {
      setSettingCoverId(null);
    }
  };

  const getThumbnailUrl = (url) => {
    if (!url) return "";
    return url;
  };

  const onSubmitReview = async () => {
    Sentry.addBreadcrumb({
      category: "user-action",
      message: "Submitted review",
      level: "info",
    });
    // The Google handoff tab was opened synchronously on click, before any of the
    // checks below ran. Every early return here must close it — only the actual
    // submit success path (below) may navigate it to Google.
    const cancelHandoff = () => {
      cancelGoogleReviewHandoff(googleWindowRef.current);
      googleWindowRef.current = null;
    };

    const data = getValues();
    // Mirrors reviewService.SUBMIT_REQUIRED exactly:
    // ["overallRating", "aiHelpfulness", "wouldRecommend", "description"].
    // Everything else is optional at submit.
    const missing = [];
    if (!data.overallRating) missing.push("an overall rating");
    if (!data.aiHelpfulness) missing.push("a rating for how helpful Romaya was");
    if (!data.description?.trim()) missing.push("your review");
    if (data.wouldRecommend === null) missing.push("whether you'd recommend this trip");

    if (missing.length > 0) {
      toast.error(`Still needed: ${missing.join(", ")}.`);
      // All four live on step 1 except wouldRecommend, which is in the step 3/4 panel.
      setCurrentStep(missing.length === 1 && missing[0].startsWith("whether") ? 4 : 1);
      cancelHandoff();
      return;
    }

    // Same rule the server enforces — catch it before the round trip and point at
    // the offending slider instead of returning a raw validation error.
    const coherenceIssues = getRatingCoherenceIssues(data);
    if (coherenceIssues.length > 0) {
      coherenceIssues.forEach((issue) =>
        setError(issue.field, { type: "client", message: issue.message }),
      );
      toast.error(coherenceIssues[0].message);
      setCurrentStep(2); // where the sub-rating sliders live
      cancelHandoff();
      return;
    }

    // Submitting is irreversible — the API rejects edits to any non-draft review and
    // offers no un-submit. Publishing runs extra pre-checks that submitting does not,
    // so verify the ones visible from here BEFORE locking anything. Otherwise a failed
    // publish strands the user with a frozen review and no community post.
    const blockers = [];
    if (trip?.status !== "completed") {
      blockers.push("Mark this trip as completed before publishing it to the community.");
    }
    // No cover-photo blocker: photos are optional and the server's publish
    // pre-checks no longer require one.
    if (blockers.length > 0) {
      setPublishBlockers(blockers);
      setReviewLockedUnpublished(false);
      toast.error("Almost there — a couple of things are needed before publishing.");
      cancelHandoff();
      return;
    }

    setPublishBlockers([]);

    try {
      setSubmitting(true);
      // Flush any debounced edit first, then read the id from the ref — saveDraft may
      // have just created the review and the state setter hasn't re-rendered yet.
      clearTimeout(autoSaveTimerRef.current);
      await saveDraft(false);

      if (!reviewIdRef.current) {
        toast.error("Couldn't save your review. Please try again.");
        cancelHandoff();
        return;
      }

      // saveDraft swallows its own errors and, on failure, leaves
      // savedSnapshotRef pointing at the last version the server accepted. If
      // what is on screen still differs, this draft did not land — and
      // submitting anyway would lock and publish that older stored text while
      // silently discarding the edit that was rejected. A blocked bad word is
      // exactly that case: the review would go live minus the user's last edit.
      if (JSON.stringify(buildDraftPayload(getValues())) !== savedSnapshotRef.current) {
        toast.error("Your latest changes haven't been saved yet, so this can't be submitted. Fix the issue above and try again.");
        cancelHandoff();
        return;
      }

      await submitReview(reviewIdRef.current);
      // Past this point the review is locked; keep local state honest so the editor
      // disables itself and auto-save stops attempting writes that would 409.
      setStatus("submitted");

      // Every submitter gets the identical Google handoff, regardless of rating —
      // do not condition this on overallRating/wouldRecommend. Fires once the review
      // has entered moderation, independent of whether the community publish below
      // succeeds — that step has its own separate, recoverable retry path.
      completeGoogleReviewHandoff(googleWindowRef.current, data.description || "");
      googleWindowRef.current = null;

      try {
        await publishTrip(tripId);
      } catch (publishErr) {
        Sentry.withScope((scope) => {
          scope.setTag("feature", "community");
          scope.setTag("app", "web");
          scope.setTag("error_source", publishErr instanceof TypeError ? "network" : "server");
          scope.setContext("review", { trip_id: tripId });
          Sentry.captureException(new CommunityError(publishErr instanceof Error ? publishErr.message : String(publishErr)));
        });
        // The review is submitted but no post exists. This is recoverable: publish is
        // idempotent and the review already satisfies its own pre-check, so surface
        // exactly what to fix rather than dropping the user on a dead end.
        console.error("[WriteReview] Publish failed after submit:", publishErr);
        const reasons = (publishErr.issues || []).map((i) => i.message).filter(Boolean);
        setPublishBlockers(reasons.length > 0 ? reasons : [publishErr.message]);
        setReviewLockedUnpublished(true);
        toast.error("Your review was saved, but publishing didn't finish.");
        return;
      }

      // Publishing submits the post to admin moderation — it is created `pending`
      // and only goes live when an admin approves it, which is also when the
      // POST_PUBLISHED credits are granted.
      //
      // No redirect: `status` is now "submitted", so the render below returns the
      // thank-you screen. Re-read the purse first so it shows what was granted
      // rather than the projection the form was working from.
      refreshRewards();
      setBurst((b) => b + 1);
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "community");
        scope.setTag("app", "web");
        scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
        scope.setContext("review", { trip_id: tripId });
        Sentry.captureException(new CommunityError(err instanceof Error ? err.message : String(err)));
      });
      cancelHandoff();
      if (err.status === 400 && err.issues) {
        mapServerIssuesToForm(err.issues, setError);
        setCurrentStep(1); // Go back to first step on error
      } else if (err.status === 429) {
         toast.error(`Slow down. Try again in ${err.retryAfter || 'a few'} seconds.`);
      } else {
        toast.error("Failed to submit review: " + err.message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleShareClick = () => {
    // Copied here, inside the gesture — Google's composer cannot be pre-filled
    // from a URL, so pasting is the fastest path, and browsers refuse clipboard
    // writes once the gesture has been spent by the awaited submit request.
    // The composer opens genuinely empty either way (Google allows no prefill),
    // so without this toast a successful copy looks identical to a failed one.
    copyReviewText(getValues("description") || "").then((ok) => {
      toast[ok ? "success" : "error"](
        ok
          ? "Review copied — paste it into the Google tab that just opened."
          : "Couldn't copy your review. Select and copy it manually before pasting into Google.",
        { toastId: "copy-review-share" },
      );
    });
    // Opened synchronously, inside the click gesture, so the later navigation
    // (after the async submit request resolves) isn't blocked as a popup.
    googleWindowRef.current = openGoogleReviewHandoffWindow();
    handleSubmit(onSubmitReview)();
  };

  // Publishing is idempotent, so retrying after the user fixes a blocker is safe and
  // is the way out of a submitted-but-unpublished review.
  const handleRetryPublish = async () => {
    if (isRetryingPublish) return;
    setIsRetryingPublish(true);
    try {
      await fetchGallery();
      await publishTrip(tripId);
      setPublishBlockers([]);
      setReviewLockedUnpublished(false);
      toast.success("Sent for review — we'll publish it once it's approved.");
      navigate("/dashboard");
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "community");
        scope.setTag("app", "web");
        scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
        scope.setContext("review", { trip_id: tripId });
        Sentry.captureException(new CommunityError(err instanceof Error ? err.message : String(err)));
      });
      console.error("[WriteReview] Retry publish failed:", err);
      const reasons = (err.issues || []).map((i) => i.message).filter(Boolean);
      setPublishBlockers(reasons.length > 0 ? reasons : [err.message]);
      toast.error("Still can't publish — see the checklist above.");
    } finally {
      setIsRetryingPublish(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
         <div className="w-10 h-10 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const isLocked = status !== "draft";

  // A submitted review is no longer editable, so there is nothing to render a form
  // for — the whole page becomes the thank-you screen, both immediately after
  // submitting and whenever the traveller opens this URL again.
  //
  // Except when publishing failed after the review locked: there is no post in the
  // moderation queue in that case, so thanking the traveller would tell them their
  // trip is with our team when nothing was ever submitted. Fall through instead, to
  // the blocker panel and its "Try publishing again" button.
  if (isLocked && !reviewLockedUnpublished && publishBlockers.length === 0) {
    return (
      <>
        <ReviewThankYou
          trip={trip}
          review={{
            description: getValues("description"),
            overallRating: getValues("overallRating"),
            userName: user?.name,
          }}
          post={post}
          mediaList={mediaList}
          rewards={rewards}
        />
        {burst > 0 && <CelebrationBurst burstKey={burst} onDone={clearBurst} />}
      </>
    );
  }

  // Credits still unclaimed on the submit action itself.
  const submitReward = (rewards?.breakdown || [])
    .filter(b => ["REVIEW_SUBMITTED", "POST_PUBLISHED"].includes(b.code) && !b.unlocked)
    .reduce((sum, b) => sum + (b.amount || 0), 0);

  // Completion is derived from the fields each step actually owns. It used to be
  // `currentStep > step.num`, i.e. "have you navigated past it" — so clicking ahead
  // to Submit ticked every earlier step green without a single answer filled in.
  const stepsMeta = [
    {
      num: 1,
      label: "Experience",
      // `!= null` so an undefined wouldRecommend counts as unanswered, matching
      // the reward projection and the server's own submit check.
      done: !!overallRating && !!watch("aiHelpfulness") && watch("wouldRecommend") != null,
      todo: "Rate your trip",
    },
    {
      num: 2,
      label: "Your Story",
      done: detailedDescriptionLen > 0,
      todo: "Write your review",
      reward: detailedBonus && !detailedBonus.unlocked ? detailedBonus.amount : null,
    },
    {
      num: 3,
      label: "Photos",
      // Optional: photos earn credits and make a far better post, but nothing
      // downstream requires them — publishing has no cover-photo pre-check.
      optional: true,
      done: uploadedPhotos.length > 0,
      todo: "Optional",
      reward: photoBonus && !photoBonus.unlocked ? photoBonus.amount : null,
    },
    {
      num: 4,
      label: "Submit",
      // Never "done" from inside the form — it is the action, not a state.
      done: false,
      todo: "Send for review",
      reward: submitReward > 0 ? submitReward : null,
    },
  ];

  // Every field is optional while writing: nothing blocks moving between steps,
  // and every step is reachable at any time. The four fields the API requires
  // (SUBMIT_REQUIRED) are still checked once, at submit, where the message names
  // exactly what is missing and jumps to it.
  const goToStep = (num) => {
    if (isLocked) return false;
    setCurrentStep(num);
    return true;
  };

  const nextStep = async () => {
    saveDraft(false);

    // Staged files upload here rather than behind their own button — leaving the
    // step with photos still staged silently discarded them.
    if (stagingFiles.some((f) => f.status !== "success")) {
      const { failed } = await startUpload();
      // Stay put on failure: advancing would strand the failed files on a step
      // the user has already left, with no way back to the retry.
      if (failed > 0) {
        toast.error(
          failed === 1
            ? "One photo didn't upload. Remove or retry it before continuing."
            : `${failed} photos didn't upload. Remove or retry them before continuing.`,
        );
        return;
      }
    }

    // Coins on every Continue. `amount: 0` means no "+N" badge rides along — the
    // flight is progress feedback here, and only a real increase in the purse
    // (handled by the effect above) is allowed to claim a figure.
    launchCoinFlight(0);
    setCurrentStep(prev => Math.min(prev + 1, TOTAL_STEPS));
  };
  const prevStep = () => setCurrentStep(prev => Math.max(prev - 1, 1));

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col font-sans pb-24 pt-24">
      {/* Stepper Navigation. Not sticky: under the fixed navbar it either slid
          beneath it or ate too much vertical space, so it scrolls with the page. */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-4 py-2.5">
            {stepsMeta.map((step, idx) => {
              const isCurrent = currentStep === step.num;
              return (
                <div key={step.num} className="relative flex flex-col items-center min-w-0">
                  {/* Connector to the next step. Cells are equal width, so a full-width
                      line from this circle's centre lands exactly on the next one.
                      top-3.5 = half the h-7 circle, keeping it through the centre. */}
                  {idx < stepsMeta.length - 1 && (
                    <span className="absolute top-3.5 left-1/2 w-full h-0.5 bg-gray-100" aria-hidden="true">
                      <span
                        className={`block h-full bg-teal-500 transition-all duration-500 ${step.done ? "w-full" : "w-0"}`}
                      />
                    </span>
                  )}

                  <button
                    type="button"
                    onClick={() => goToStep(step.num)}
                    aria-current={isCurrent ? "step" : undefined}
                    className="relative z-10 flex flex-col items-center gap-1 px-2 group cursor-pointer disabled:cursor-default"
                    disabled={isLocked}
                  >
                    <span
                      // No `bg-*` in the base classes: a base `bg-white` and a
                      // conditional `bg-teal-600` are the same specificity, so the
                      // winner came down to stylesheet order rather than this ternary.
                      // Each branch sets its own background.
                      className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 transition-all ${
                        step.done
                          ? "bg-teal-600 border-teal-600 text-white animate-check-flip"
                          : isCurrent
                            ? "bg-white border-teal-600 text-teal-700 ring-2 ring-teal-100"
                            : "bg-white border-gray-200 text-gray-400 group-hover:border-gray-300"
                      }`}
                    >
                      {step.done ? <Check className="w-3.5 h-3.5" strokeWidth={3} /> : step.num}
                    </span>

                    <span
                      className={`text-xs whitespace-nowrap transition-colors ${
                        isCurrent
                          ? "font-bold text-teal-700"
                          : step.done
                            ? "font-semibold text-gray-700"
                            : "font-medium text-gray-400"
                      }`}
                    >
                      {step.label}
                    </span>

                    {/* What this step is still worth. Amounts come from the live credit
                        rules, and the chip disappears the moment the bonus is granted. */}
                    <span className="h-4 flex items-center">
                      {step.reward ? (
                        <span className="animate-reward-pop inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-[10px] font-bold text-amber-700">
                          <Gift className="w-2.5 h-2.5" /> +{step.reward}
                        </span>
                      ) : !step.done && !isCurrent ? (
                        <span className="text-[10px] text-gray-300 font-medium truncate max-w-[8rem]">
                          {step.todo}
                        </span>
                      ) : null}
                    </span>
                  </button>
                </div>
              );
            })}
          </div>

          {/* Save status moved to the sticky action bar at the bottom. Both were
              on screen at once, showing the same state twice. */}
        </div>
      </div>

      {/* Content Area */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 w-full">
        {/* Feedback progress + what it pays. Sits above every step so the reward
            for continuing is visible at all times, not only on the last screen. */}
        {!isLocked && (
          <div className="mb-6 flex flex-col md:flex-row gap-4 items-stretch">
            <div className="flex-1 bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div>
                  {/* Names the trip being reviewed — the hero that used to carry the
                      destination was removed, and nothing else on this step says it. */}
                  <h2 className="text-lg font-bold text-teal-900">
                    {trip?.destination ? `Review your trip to ${trip.destination}` : "Your Trip Feedback"}
                  </h2>
                  <p className="text-sm text-gray-500">Help others roam better and earn rewards.</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                    Step {currentStep} of {TOTAL_STEPS}
                  </p>
                  <p className="text-xl font-bold text-teal-700 leading-tight">{stepProgress}%</p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <span className="text-xs font-semibold text-gray-500 w-28 shrink-0">Step Progress</span>
                <span className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <span
                    className="block h-full bg-teal-600 rounded-full transition-all duration-500"
                    style={{ width: `${stepProgress}%` }}
                  />
                </span>
              </div>

              {bonusHint && (
                <p className="mt-3 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-100 px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5">
                  <Gift className="w-3.5 h-3.5 shrink-0" /> {bonusHint}
                </p>
              )}
            </div>

            {/* Potential earnings. The figure is whatever the credit rules say right
                now — admins change them at runtime, so it is never hard-coded. The
                unit is "RoamPoints" everywhere per the Round 12 currency lock, even
                though the design mock still says Miles. */}
            {rewards && (
              <div
                ref={purseRef}
                // Re-keyed on each catch so the class re-applies and the animation
                // replays on every landing, not just the first.
                key={`purse-${walletCatch}`}
                className={`md:w-64 shrink-0 bg-gradient-to-br from-teal-700 to-teal-800 rounded-2xl shadow-sm p-4 flex items-center gap-3.5 ${
                  walletCatch > 0 ? "wallet-catch" : ""
                }`}
              >
                <AnimatedRewardGift className="w-14 h-14 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold text-teal-100 uppercase tracking-wider">
                    You&apos;ve secured
                  </p>
                  {/* Counts up as the coins land — see the displayedCredits effect. */}
                  <p className="text-2xl font-bold text-white leading-tight">
                    {displayedCredits}
                    <span className="text-sm font-semibold text-teal-200">
                      {" "}/ {rewardCeiling} {rewards.currency || "RoamPoints"}
                    </span>
                  </p>
                  <span className="mt-1.5 block h-1.5 w-full bg-white/15 rounded-full overflow-hidden">
                    <span
                      className="block h-full bg-amber-300 rounded-full transition-all duration-500"
                      style={{ width: `${rewardCeiling > 0 ? Math.round((displayedCredits / rewardCeiling) * 100) : 0}%` }}
                    />
                  </span>
                  {/* Names the very next reward and what unlocks it — a purse that
                      sits at 0 must explain itself, not just show a total. */}
                  {nextRewardHint?.cta ? (
                    <button
                      type="button"
                      onClick={() => goToStep(nextRewardHint.step)}
                      className="mt-1.5 text-[11px] text-teal-100 leading-snug truncate w-full text-left hover:text-white transition-colors cursor-pointer"
                    >
                      <span className="font-bold text-amber-300">+{nextRewardHint.amount}</span>{" "}
                      next · <span className="underline underline-offset-2">{nextRewardHint.cta}</span> →
                    </button>
                  ) : rewardCeiling - displayedCredits > 0 ? (
                    <p className="text-[11px] text-teal-100 mt-1.5 leading-snug truncate">
                      <span className="font-bold text-amber-300">
                        +{rewardCeiling - displayedCredits}
                      </span>{" "}
                      still up for grabs
                    </p>
                  ) : (
                    <p className="text-[11px] font-semibold text-amber-300 mt-1.5">
                      Every bonus secured 🎉
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {publishBlockers.length > 0 && (
          <div className="mb-6 p-5 bg-red-50 border border-red-200 rounded-xl">
            <div className="flex items-start gap-3">
              <span className="text-xl leading-none" aria-hidden="true">⚠</span>
              <div className="flex-1">
                <h3 className="font-bold text-red-900 mb-1">
                  {reviewLockedUnpublished
                    ? "Your review was saved, but it isn't published yet"
                    : "Before you can publish"}
                </h3>
                <p className="text-sm text-red-800 mb-3">
                  {reviewLockedUnpublished
                    ? "Nothing is lost. Fix the item(s) below, then publish again — from here or from your dashboard."
                    : "Sending your post for review needs these first. Once approved, it goes live on the community page."}
                </p>
                <ul className="space-y-1.5 mb-4">
                  {publishBlockers.map((reason, i) => (
                    <li key={i} className="text-sm text-red-800 flex items-start gap-2">
                      <span className="text-red-400 mt-0.5" aria-hidden="true">•</span>
                      <span>{reason}</span>
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap items-center gap-3">
                  {reviewLockedUnpublished ? (
                    <>
                      <button
                        type="button"
                        onClick={handleRetryPublish}
                        disabled={isRetryingPublish}
                        className="px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 disabled:opacity-60 transition-colors cursor-pointer"
                      >
                        {isRetryingPublish ? "Publishing…" : "Try publishing again"}
                      </button>
                      <button
                        type="button"
                        onClick={() => navigate("/dashboard")}
                        className="text-sm font-semibold text-red-700 hover:text-red-900 underline cursor-pointer"
                      >
                        Go to dashboard
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => { setPublishBlockers([]); goToStep(3); }}
                      className="px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-lg hover:bg-red-700 transition-colors cursor-pointer"
                    >
                      Go to Photos
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* No "review submitted" banner here — a locked review returns the
            thank-you screen above and never reaches this form. */}

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
          
          {/* LEFT COLUMN */}
          <div className="col-span-1 md:col-span-8 space-y-8">
            
            {currentStep === 1 && (
              <>
                {/* Overall Experience — the two headline ratings side by side. */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-7">
                  <h3 className="text-lg font-bold text-teal-900 mb-5">Overall Experience</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <StarField
                        label="Overall Trip Rating"
                        name="overallRating"
                        control={control}
                        disabled={isLocked}
                      />
                      {errors.overallRating && (
                        <p className="mt-2 text-sm text-red-500 font-semibold">{errors.overallRating.message}</p>
                      )}
                    </div>
                    <StarField
                      label="Itinerary Satisfaction"
                      name="itinerarySatisfaction"
                      control={control}
                      disabled={isLocked}
                    />
                  </div>
                </div>

                {/* AI Planning Experience — the north-star KPI. */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-7">
                  <div className="flex items-center gap-2 mb-4">
                    <h3 className="text-lg font-bold text-teal-900">AI Planning Experience</h3>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-teal-50 border border-teal-100 text-teal-700 text-[10px] font-bold">
                      <Sparkles className="w-3 h-3" /> AI Powered
                    </span>
                  </div>
                  <p className="text-sm font-medium text-teal-700 mb-4">
                    How helpful was Romaya while planning your trip?
                  </p>
                  <Controller
                    name="aiHelpfulness"
                    control={control}
                    render={({ field }) => (
                      <PillGroup
                        options={AI_HELPFULNESS_OPTIONS}
                        value={field.value || null}
                        // PillGroup clears to null; this field's unset value is 0.
                        onChange={(v) => field.onChange(v ?? 0)}
                        disabled={isLocked}
                      />
                    )}
                  />
                  {errors.aiHelpfulness && (
                    <p className="mt-3 text-sm text-red-500 font-semibold">{errors.aiHelpfulness.message}</p>
                  )}
                </div>

                {/* Hotel Experience */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-7">
                  <h3 className="text-lg font-bold text-teal-900 mb-5">Hotel Experience</h3>
                  <StarField
                    label="Rate your hotel recommendations"
                    name="hotelRating"
                    control={control}
                    disabled={isLocked}
                    issue={ratingIssueByField.hotelRating}
                  />
                </div>

                {/* Transport Experience */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-7">
                  <h3 className="text-lg font-bold text-teal-900 mb-5">Transport Experience</h3>
                  <StarField
                    label="Rate how you got around"
                    name="transportRating"
                    control={control}
                    disabled={isLocked}
                    issue={ratingIssueByField.transportRating}
                  />
                </div>

                {/* Food Experience — the rating and the vegetarian question together. */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-7">
                  <h3 className="text-lg font-bold text-teal-900 mb-5">Food Experience</h3>
                  <div className="space-y-6">
                    <StarField
                      label="Rate the food on your trip"
                      name="foodRating"
                      control={control}
                      disabled={isLocked}
                      issue={ratingIssueByField.foodRating}
                    />
                    <div className="pt-6 border-t border-gray-50">
                      <p className="text-sm font-medium text-teal-700 mb-3">
                        {ENUM_FIELDS.vegetarianOptions.question}
                      </p>
                      <Controller
                        name="vegetarianOptions"
                        control={control}
                        render={({ field }) => (
                          <PillGroup
                            options={ENUM_FIELDS.vegetarianOptions.options}
                            value={field.value}
                            onChange={field.onChange}
                            disabled={isLocked}
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>

                {/* Itinerary — the three itinerary questions in one place. */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-7">
                  <h3 className="text-lg font-bold text-teal-900 mb-5">Itinerary</h3>
                  <div className="space-y-6">
                    <StarField
                      label="Was the itinerary easy to follow?"
                      name="itineraryEasyToFollow"
                      control={control}
                      disabled={isLocked}
                    />
                    {["itineraryCoverage", "itineraryPace"].map((name) => (
                      <div key={name} className="pt-6 border-t border-gray-50">
                        <p className="text-sm font-medium text-teal-700 mb-3">
                          {ENUM_FIELDS[name].question}
                        </p>
                        <Controller
                          name={name}
                          control={control}
                          render={({ field }) => (
                            <PillGroup
                              options={ENUM_FIELDS[name].options}
                              value={field.value}
                              onChange={field.onChange}
                              disabled={isLocked}
                            />
                          )}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Recommendation */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-7">
                  <h3 className="text-lg font-bold text-teal-900 mb-4">Recommendation</h3>
                  <p className="text-sm font-medium text-teal-700 mb-4">Would you recommend this itinerary?</p>
                  <Controller
                    name="wouldRecommend"
                    control={control}
                    render={({ field }) => (
                      <div className="grid grid-cols-2 gap-4">
                        <button
                          type="button"
                          disabled={isLocked}
                          onClick={() => field.onChange(field.value === true ? null : true)}
                          className={`py-6 rounded-xl border-2 font-bold flex flex-col items-center gap-2 transition-all ${
                            field.value === true
                              ? "bg-teal-50 border-teal-600 text-teal-700 shadow-sm"
                              : "bg-white border-gray-200 text-gray-500 hover:border-gray-300"
                          } ${isLocked ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}`}
                        >
                          <ThumbsUp className="w-6 h-6" />
                          <span className="text-sm">Absolutely</span>
                        </button>
                        <button
                          type="button"
                          disabled={isLocked}
                          onClick={() => field.onChange(field.value === false ? null : false)}
                          className={`py-6 rounded-xl border-2 font-bold flex flex-col items-center gap-2 transition-all ${
                            field.value === false
                              ? "bg-red-50 border-red-500 text-red-700 shadow-sm"
                              : "bg-white border-gray-200 text-gray-500 hover:border-gray-300"
                          } ${isLocked ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}`}
                        >
                          <ThumbsDown className="w-6 h-6" />
                          <span className="text-sm">No Thanks</span>
                        </button>
                      </div>
                    )}
                  />
                  {errors.wouldRecommend && (
                    <p className="mt-3 text-sm text-red-500">{errors.wouldRecommend.message}</p>
                  )}
                </div>
              </>
            )}

            {currentStep === 2 && (
              <>
                {/* Value & Experience */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-7">
                  <h3 className="text-lg font-bold text-teal-900 mb-5 flex items-center gap-2">
                    <span className="w-7 h-7 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center">
                      <Gift className="w-4 h-4" />
                    </span>
                    Value &amp; Experience
                  </h3>

                  <div className="space-y-7">
                    <div>
                      <p className="text-sm font-medium text-teal-700 mb-3">
                        {ENUM_FIELDS.dealPerception.question}
                      </p>
                      <Controller
                        name="dealPerception"
                        control={control}
                        render={({ field }) => (
                          <FacePillGroup
                            options={ENUM_FIELDS.dealPerception.options}
                            value={field.value}
                            onChange={field.onChange}
                            disabled={isLocked}
                          />
                        )}
                      />
                    </div>

                    <div className="pt-6 border-t border-gray-50">
                      <StarField
                        label="Was your trip worth the money?"
                        name="valueForMoney"
                        control={control}
                        disabled={isLocked}
                      />
                      {!watch("valueForMoney") && (
                        <p className="mt-2 text-xs text-gray-400 flex items-center gap-1.5">
                          <Star className="w-3 h-3" /> Tap a star to rate your value experience
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Tell Your Story */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-7">
                  <h3 className="text-lg font-bold text-teal-900 mb-5 flex items-center gap-2">
                    <span className="w-7 h-7 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center">
                      <Sparkles className="w-4 h-4" />
                    </span>
                    Tell Your Story
                  </h3>

                  <div className="space-y-7">
                    {/* `description` is not in the design mock, but it is one of the four
                        fields the API requires at submit and it is the body of the public
                        post — so it leads this card rather than having no input at all. */}
                    <div>
                      <div className="flex justify-between items-center mb-2">
                        <label className="text-sm font-medium text-teal-700">
                          Tell us about your trip <span className="text-gray-400 font-normal">(required)</span>
                        </label>
                        <span className={`text-xs font-medium ${(descriptionContent || "").length > 5000 ? "text-red-500" : "text-gray-400"}`}>
                          {(descriptionContent || "").length} characters
                        </span>
                      </div>
                      <textarea
                        rows={7}
                        {...register("description")}
                        disabled={isLocked}
                        placeholder="Describe the moments that made your trip special..."
                        className="w-full px-4 py-3 bg-gray-50 border-transparent focus:bg-white border rounded-xl focus:border-teal-500 focus:ring-0 transition-all resize-none text-gray-900 leading-relaxed"
                      />
                      {errors.description && <p className="mt-1 text-sm text-red-500">{errors.description.message}</p>}

                      {/* Progress toward the 200-char bonus, against the field it applies to. */}
                      {!isLocked && detailedDescriptionLen < DETAILED_DESCRIPTION_MIN && detailedBonus?.amount > 0 && !detailedBonus.unlocked && (
                        <div className="mt-3 flex items-center gap-3">
                          <span className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <span
                              className="block h-full bg-amber-400 rounded-full transition-all duration-300"
                              style={{ width: `${Math.min(100, (detailedDescriptionLen / DETAILED_DESCRIPTION_MIN) * 100)}%` }}
                            />
                          </span>
                          <span className="text-xs font-semibold text-amber-700 shrink-0">
                            {DETAILED_DESCRIPTION_MIN - detailedDescriptionLen} more to unlock +{detailedBonus.amount}
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="pt-6 border-t border-gray-50">
                      <div className="flex justify-between items-center mb-2">
                        <label className="text-sm font-medium text-teal-700">What was your favourite part?</label>
                        <span className="text-xs text-gray-400 font-medium">
                          {(watch("bestExperience") || "").length} characters
                        </span>
                      </div>
                      <textarea
                        rows={5}
                        {...register("bestExperience")}
                        disabled={isLocked}
                        placeholder="Describe the moments that made your trip special..."
                        className="w-full px-4 py-3 bg-gray-50 border-transparent focus:bg-white border rounded-xl focus:border-teal-500 focus:ring-0 transition-all resize-none text-gray-900 text-sm leading-relaxed"
                      />

                      {!isLocked && (
                        <div className="mt-3">
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">
                            Quick prompts
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {STORY_PROMPTS.map((prompt) => (
                              <button
                                key={prompt}
                                type="button"
                                onClick={() => applyStoryPrompt(prompt)}
                                className="px-3 py-1.5 rounded-lg bg-gray-50 border border-gray-200 text-xs font-medium text-gray-600 hover:border-teal-300 hover:bg-teal-50 hover:text-teal-700 transition-colors cursor-pointer"
                              >
                                {prompt}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="pt-6 border-t border-gray-50">
                      <label className="block text-sm font-medium text-teal-700 mb-2">What can we improve?</label>
                      <textarea
                        rows={4}
                        {...register("improvements")}
                        disabled={isLocked}
                        placeholder="How can we make your next trip even more seamless?"
                        className="w-full px-4 py-3 bg-gray-50 border-transparent focus:bg-white border rounded-xl focus:border-teal-500 focus:ring-0 transition-all resize-none text-gray-900 text-sm"
                      />
                    </div>

                    {/* Only asked when the trip fell short — see spec section 1. */}
                    {showWhatWentWrong && (
                      <div className="p-5 bg-amber-50 border border-amber-100 rounded-2xl">
                        <div className="flex justify-between mb-2">
                          <label className="flex items-center gap-2 text-xs font-bold text-amber-800 uppercase tracking-wider">
                            <AlertTriangle className="w-4 h-4" /> What went wrong?
                          </label>
                          <span className="text-xs text-amber-600 font-medium">
                            {(watch("whatWentWrong") || "").length} / 2000
                          </span>
                        </div>
                        <p className="text-xs text-amber-700 mb-3">
                          This stays between you and our team — it isn't shown on your public post.
                        </p>
                        <textarea
                          rows={4}
                          {...register("whatWentWrong")}
                          disabled={isLocked}
                          placeholder="Tell us what let the trip down, so we can fix it."
                          className="w-full px-4 py-3 bg-white border border-amber-200 rounded-xl focus:border-amber-500 focus:ring-0 transition-all resize-none text-gray-900 text-sm"
                        />
                        {errors.whatWentWrong && <p className="mt-1 text-sm text-red-500">{errors.whatWentWrong.message}</p>}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {currentStep === 3 && (
              <>
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-7">
                  <h3 className="text-lg font-bold text-teal-900 mb-1 flex items-center gap-2">
                    <span className="w-7 h-7 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center">
                      <Camera className="w-4 h-4" />
                    </span>
                    Share Your Favourite Moments
                  </h3>
                  <p className="text-sm text-gray-500 mb-6">
                    Show the best memories from your trip. Optional — you can submit without
                    them, though photos earn RoamPoints and get far more views.
                  </p>

                  {/* Counts and ceilings come from GET /api/media/limits — never a
                      hard-coded "10", which is exactly what the spec calls out. */}
                  <div className="flex flex-wrap gap-6 mb-6 text-sm font-medium text-gray-600 bg-gray-50 p-4 rounded-xl border border-gray-100">
                    <div>
                      Photos:{" "}
                      <span className={uploadedPhotos.length >= limits.photosPerTrip ? "text-red-500 font-bold" : "text-gray-900 font-bold"}>
                        {uploadedPhotos.length} / {limits.photosPerTrip}
                      </span>
                    </div>
                    <div>
                      Videos:{" "}
                      <span className={uploadedVideos.length >= limits.videosPerTrip ? "text-red-500 font-bold" : "text-gray-900 font-bold"}>
                        {uploadedVideos.length} / {limits.videosPerTrip}
                      </span>
                    </div>
                    <div className="ml-auto flex items-center gap-1.5 text-teal-700">
                      <Check className={`w-4 h-4 ${hasCoverPhoto ? "opacity-100" : "opacity-25"}`} />
                      {hasCoverPhoto ? "Cover photo set" : "Cover photo optional"}
                    </div>
                  </div>

                  {/* Upload Trigger */}
                  {!isLocked && (
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                      onDragLeave={() => setIsDragging(false)}
                      onDrop={handleDrop}
                      className={`border-2 border-dashed rounded-2xl p-10 text-center transition-all cursor-pointer mb-8 group ${
                        isDragging ? "border-teal-500 bg-teal-50" : "border-gray-300 hover:bg-gray-50 hover:border-teal-400"
                      }`}
                    >
                      <div className="w-16 h-16 bg-gray-100 text-gray-400 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-teal-50 group-hover:text-teal-600 transition-colors">
                        <Camera className="w-7 h-7" />
                      </div>
                      <p className="font-medium text-gray-700 mb-1">Click or drag photos here</p>
                      <p className="text-sm text-gray-500 mb-4">
                        Upload up to {limits.photosPerTrip} high-quality photos from your adventure
                        {" · "}max {formatBytes(limits.maxPhotoBytes)} each
                      </p>
                      <span className="px-6 py-2 bg-gray-100 text-gray-700 font-semibold rounded-full group-hover:bg-teal-600 group-hover:text-white transition-colors">
                        Select files
                      </span>
                      <input
                        type="file"
                        multiple
                        accept="image/*,video/*"
                        className="hidden"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        disabled={isUploading}
                      />
                    </div>
                  )}

                  {/* Staging Area */}
                  {stagingFiles.length > 0 && (
                    <div className="mb-8 p-6 bg-teal-50/40 rounded-2xl border border-teal-100">
                      <div className="mb-4">
                        <h4 className="font-bold text-teal-900">
                          Ready to upload ({stagingFiles.length})
                        </h4>
                        <p className="text-xs text-teal-700/70 mt-0.5">
                          {isUploading
                            ? "Uploading your photos…"
                            : "These upload automatically when you press Continue."}
                        </p>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {stagingFiles.map(sf => (
                          <div key={sf.id} className="relative rounded-xl overflow-hidden bg-white border border-gray-200">
                            {sf.kind === "video" ? (
                              <video src={sf.preview} className="w-full h-24 object-cover" />
                            ) : (
                              <img src={sf.preview} alt="" className="w-full h-24 object-cover" />
                            )}
                            <div className="p-2">
                              <select
                                value={sf.category}
                                onChange={(e) => setStagingFiles(prev => prev.map(f => f.id === sf.id ? { ...f, category: e.target.value } : f))}
                                disabled={isUploading || sf.status === "success" || sf.kind !== "photo"}
                                className="w-full text-xs border-gray-300 rounded focus:ring-teal-500"
                              >
                                {sf.kind === "photo"
                                  ? orderCategories(limits.photoCategories).map(cat => (
                                      <option key={cat} value={cat}>{categoryLabel(cat)}</option>
                                    ))
                                  : orderCategories(limits.videoCategories).map(cat => (
                                      <option key={cat} value={cat}>{categoryLabel(cat)}</option>
                                    ))
                                }
                              </select>
                              {sf.status === "uploading" && <div className="h-1 bg-teal-600 mt-2 rounded" style={{width: `${sf.progress}%`}} />}
                              {sf.status === "success" && <div className="text-[10px] text-green-600 font-bold mt-1 text-center">Done</div>}
                              {sf.status === "error" && <div className="text-[10px] text-red-600 font-bold mt-1 text-center truncate">{sf.error}</div>}
                            </div>
                            {!isUploading && sf.status !== "success" && (
                              <button onClick={() => setStagingFiles(prev => {
                                const target = prev.find(f => f.id === sf.id);
                                if (target) URL.revokeObjectURL(target.preview);
                                return prev.filter(f => f.id !== sf.id);
                              })} className="absolute top-1 right-1 w-6 h-6 bg-black/50 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-500 cursor-pointer">×</button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Preview gallery. Captions are supported by PATCH /api/media/:id and
                      had no UI until now — they are what turns a photo grid into a story. */}
                  {mediaList.length > 0 && (
                    <div>
                      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-3">
                        Preview gallery
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {mediaList.map(m => (
                          <div key={m.id} className="rounded-xl border border-gray-200 overflow-hidden bg-white">
                            <div className="relative aspect-[4/3] group bg-gray-100">
                              {m.kind === "video" ? (
                                <video src={m.publicUrl || m.url} className="w-full h-full object-cover" />
                              ) : (
                                <img src={getThumbnailUrl(m.thumbUrl || m.publicUrl || m.url)} alt="" className="w-full h-full object-cover" />
                              )}

                              {m.category === "cover" && (
                                <div className="absolute top-2 left-2 px-2 py-0.5 bg-teal-600 text-white rounded-full text-[9px] font-bold flex items-center gap-1">
                                  <Check className="w-2.5 h-2.5" /> Cover
                                </div>
                              )}

                              {!isLocked && m.kind === "photo" && m.category !== "cover" && (
                                <button
                                  type="button"
                                  onClick={() => handleSetCover(m.id)}
                                  disabled={settingCoverId !== null}
                                  className="absolute bottom-2 left-2 right-2 py-1.5 bg-white/95 text-gray-800 rounded-lg text-[11px] font-semibold opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white disabled:opacity-50 cursor-pointer"
                                >
                                  {settingCoverId === m.id ? "Setting…" : "Set as cover"}
                                </button>
                              )}

                              {!isLocked && (
                                <button
                                  onClick={() => handleDeleteMedia(m.id)}
                                  className="absolute top-2 right-2 w-6 h-6 bg-black/50 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-xs hover:bg-red-500 cursor-pointer"
                                >
                                  ×
                                </button>
                              )}
                            </div>

                            <div className="p-3">
                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                                {categoryLabel(m.category)}
                              </p>
                              {isLocked ? (
                                m.caption && <p className="text-xs text-gray-600">{m.caption}</p>
                              ) : (
                                <input
                                  type="text"
                                  defaultValue={m.caption || ""}
                                  maxLength={CAPTION_MAX}
                                  placeholder="Add a caption…"
                                  onBlur={(e) => handleCaptionSave(m.id, e.target.value)}
                                  className="w-full text-xs px-2 py-1.5 bg-gray-50 border border-transparent rounded-lg focus:bg-white focus:border-teal-400 focus:ring-0 transition-all text-gray-700"
                                />
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Video — its own picker, and its own limits straight from the API. */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-gray-900 text-white flex items-center justify-center shrink-0">
                    <Video className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 text-sm">Share a video snippet?</p>
                    <p className="text-xs text-gray-500">
                      Videos bring your trip to life. Max {limits.maxVideoSeconds}s
                      {" · "}{formatBytes(limits.maxVideoBytes)}
                      {" · "}up to {limits.videosPerTrip}
                    </p>
                  </div>
                  {!isLocked && (
                    <>
                      <button
                        type="button"
                        onClick={() => videoInputRef.current?.click()}
                        disabled={isUploading || uploadedVideos.length >= limits.videosPerTrip}
                        className="px-4 py-2 rounded-lg bg-teal-50 text-teal-700 text-sm font-semibold hover:bg-teal-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer shrink-0"
                      >
                        Upload video
                      </button>
                      <input
                        type="file"
                        accept="video/*"
                        className="hidden"
                        ref={videoInputRef}
                        onChange={handleFileSelect}
                        disabled={isUploading}
                      />
                    </>
                  )}
                </div>

                {/* What publishing means. This is deliberately a statement, not a toggle:
                    there is no per-review feature-consent field in the API (the schema
                    migration says so outright), so a switch here would look like it
                    controlled something and silently control nothing. */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 flex items-start gap-3">
                  <span className="w-9 h-9 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center shrink-0">
                    <Users className="w-4 h-4" />
                  </span>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">
                      Your review and photos go to our team for review
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Once approved, this trip appears on the community page for other
                      travellers to find. You can unpublish it any time from your dashboard.
                    </p>
                  </div>
                </div>
              </>
            )}

            {currentStep === 4 && (
              <>
                {/* The claim moment: what submitting right now pays, and what's still
                    being left behind — with one-tap routes back to grab it. */}
                {!isLocked && rewards && (
                  <div className="bg-gradient-to-br from-teal-700 to-teal-800 rounded-2xl shadow-sm p-6 text-white">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <p className="text-[10px] font-bold text-teal-100 uppercase tracking-wider mb-1">
                          Submitting now earns you
                        </p>
                        <p className="text-3xl font-bold leading-none">
                          +{claimableCredits}
                          <span className="text-base font-semibold text-teal-200">
                            {" "}{rewards.currency || "RoamPoints"}
                          </span>
                        </p>
                        {approvalCredits > 0 && (
                          <p className="text-[11px] text-teal-100 mt-1.5">
                            +{approvalCredits} more once your post is approved
                          </p>
                        )}
                      </div>
                      <div className="w-11 h-11 rounded-full bg-white/15 flex items-center justify-center shrink-0">
                        <Gift className="w-5 h-5" />
                      </div>
                    </div>

                    {missedCredits > 0 && (
                      <div className="mt-5 pt-4 border-t border-white/15">
                        <p className="text-xs font-semibold text-amber-300 mb-2.5">
                          You&apos;re leaving +{missedCredits} {rewards.currency || "RoamPoints"} behind:
                        </p>
                        <ul className="space-y-2">
                          {missedRewards.map((b) => {
                            const action = rewardGap(b.code) || REWARD_ACTIONS[b.code];
                            return (
                              <li key={b.code} className="flex items-center gap-3 text-xs">
                                <span className="flex-1 text-teal-100">{rewardLabel(b)}</span>
                                {b.amount > 0 && (
                                  <span className="font-bold text-amber-300">+{b.amount}</span>
                                )}
                                {action && (
                                  <button
                                    type="button"
                                    onClick={() => goToStep(action.step)}
                                    className="px-2.5 py-1 rounded-lg bg-white/15 hover:bg-white/25 font-semibold transition-colors cursor-pointer"
                                  >
                                    {action.cta}
                                  </button>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

              {/* Preview of the public post. Laid out as the published card rather
                  than as a form summary — this is the last thing seen before the
                  review goes live, so it should look like what goes live. */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-6 py-3 border-b border-gray-100 flex items-center justify-between gap-3">
                  <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                    Preview
                  </p>
                  <p className="text-[11px] text-gray-400">How your post will appear to travellers</p>
                </div>

                {/* Cover image, or a neutral band when none is set yet. */}
                <div className="relative bg-gray-100">
                  {coverPhoto ? (
                    <img
                      src={getThumbnailUrl(coverPhoto.publicUrl || coverPhoto.url || coverPhoto.thumbUrl)}
                      alt=""
                      className="w-full h-52 sm:h-64 object-cover"
                    />
                  ) : (
                    <div className="w-full h-32 flex flex-col items-center justify-center gap-2 text-gray-300 bg-gradient-to-br from-gray-50 to-gray-100">
                      <Camera className="w-7 h-7" />
                      <span className="text-xs font-medium text-gray-400">No cover photo yet</span>
                    </div>
                  )}
                  {coverPhoto && (
                    <>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/15 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 p-6">
                        <h2 className="text-xl sm:text-2xl font-bold text-white leading-snug">
                          {trip?.destination || "Your trip"}
                        </h2>
                      </div>
                    </>
                  )}
                </div>

                <div className="p-6">
                  {/* Title lives here when there is no cover to overlay it on. */}
                  {!coverPhoto && (
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 leading-snug mb-3">
                      {trip?.destination || "Your trip"}
                    </h2>
                  )}

                  {/* Headline rating + the trip facts that frame it. */}
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 pb-5 border-b border-gray-100">
                    {watch("overallRating") ? (
                      <span className="inline-flex items-center gap-2">
                        <StarDisplay value={watch("overallRating")} size="lg" />
                        <span className="text-sm font-bold text-gray-900">
                          {watch("overallRating")}.0
                        </span>
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">Not rated yet</span>
                    )}

                    {tripDates && (
                      <span className="text-xs text-gray-500">
                        {tripDates.range}
                        {tripDates.nights > 0 && ` · ${tripDates.nights} night${tripDates.nights === 1 ? "" : "s"}`}
                      </span>
                    )}

                    {watch("wouldRecommend") === true && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-teal-50 text-teal-700 text-[11px] font-semibold">
                        <ThumbsUp className="w-3 h-3" /> Recommends this trip
                      </span>
                    )}
                  </div>

                  {/* Review body. */}
                  <div className="py-5">
                    {watch("description")?.trim() ? (
                      <p className="text-[15px] text-gray-700 leading-relaxed whitespace-pre-wrap">
                        {watch("description")}
                      </p>
                    ) : (
                      <p className="text-sm text-gray-400 italic">
                        Your written review will appear here.
                      </p>
                    )}
                  </div>

                  {(watch("bestExperience")?.trim() || watch("improvements")?.trim()) && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pb-5">
                      {watch("bestExperience")?.trim() && (
                        <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-4">
                          <h4 className="text-[11px] font-bold text-teal-700 uppercase tracking-wider mb-1.5">
                            Highlight
                          </h4>
                          <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                            {watch("bestExperience")}
                          </p>
                        </div>
                      )}
                      {watch("improvements")?.trim() && (
                        <div className="rounded-xl border border-gray-100 bg-gray-50/70 p-4">
                          <h4 className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1.5">
                            Room to improve
                          </h4>
                          <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                            {watch("improvements")}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Category ratings, as scored rows rather than chips. Only the
                      ones actually set — the old version defaulted the three
                      sub-ratings to 3/5 and showed a score nobody gave. */}
                  {previewRatings.length > 0 && (
                    <div className="pt-5 border-t border-gray-100">
                      <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-3">
                        Detailed ratings
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5">
                        {previewRatings.map((r) => (
                          <div key={r.name} className="flex items-center gap-3">
                            <r.Icon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                            <span className="text-sm text-gray-600 flex-1 min-w-0 truncate">{r.label}</span>
                            <StarDisplay value={watch(r.name)} />
                            <span className="text-xs font-semibold text-gray-900 w-3 text-right">
                              {watch(r.name)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Photos, as a gallery strip rather than a file list. */}
                  {mediaList.length > 0 && (
                    <div className="pt-5 mt-5 border-t border-gray-100">
                      <h4 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-3">
                        {mediaList.length} {mediaList.length === 1 ? "photo & video" : "photos & videos"}
                      </h4>
                      <div className="flex gap-2.5 overflow-x-auto pb-1">
                        {mediaList.map(m => (
                          <div
                            key={m.id}
                            className="w-24 h-24 shrink-0 rounded-xl overflow-hidden bg-gray-100 border border-gray-200 relative"
                          >
                            {m.kind === "video" ? (
                              <video src={m.publicUrl || m.url} className="w-full h-full object-cover" />
                            ) : (
                              <img
                                src={getThumbnailUrl(m.thumbUrl || m.publicUrl || m.url)}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            )}
                            {m.category === "cover" && (
                              <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded-full bg-white/95 text-[9px] font-bold text-gray-700">
                                Cover
                              </span>
                            )}
                            {m.kind === "video" && (
                              <span className="absolute inset-0 flex items-center justify-center bg-black/25">
                                <Video className="w-5 h-5 text-white" />
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* `whatWentWrong` is private feedback — kept visually outside the
                    post card so it never looks like part of what gets published. */}
                {watch("whatWentWrong")?.trim() && (
                  <div className="px-6 py-4 bg-amber-50/70 border-t border-amber-100">
                    <h4 className="text-xs font-bold text-amber-900 mb-1 flex items-center gap-1.5">
                      <AlertTriangle className="w-3.5 h-3.5" /> Private note to the Romaya team
                    </h4>
                    <p className="text-[11px] text-amber-700 mb-2">Not shown on your public post.</p>
                    <p className="text-sm text-amber-900 whitespace-pre-wrap">{watch("whatWentWrong")}</p>
                  </div>
                )}
              </div>
              </>
            )}
          </div>

          {/* RIGHT COLUMN. Only the fixed navbar (4/5rem) needs clearing now that
              the stepper scrolls away with the page. */}
          <div className="col-span-1 md:col-span-4 space-y-8 sticky top-24 h-fit">
            
            {/* Rewards progress. Every amount, label and unlock state is rendered from
                the API response — admins change credit rules at runtime, so nothing
                here is hard-coded. The unit is "RoamPoints" per the Round 12 currency
                lock, even though the design mock still says Miles. */}
            {rewards && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="bg-teal-800 px-5 py-3 flex items-center justify-between">
                  <h3 className="text-sm font-bold text-white tracking-tight">
                    Rewards Progress
                  </h3>
                  <Gift className="w-4 h-4 text-teal-200" />
                </div>

                <div className="p-4">
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <div>
                      <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-1">
                        Secured so far
                      </p>
                      <p className="text-2xl font-bold text-teal-900 leading-none">
                        {displayedCredits}
                        <span className="text-sm font-semibold text-gray-400">
                          {" "}/ {rewardCeiling} {rewards.currency || "RoamPoints"}
                        </span>
                      </p>
                    </div>

                    {/* The ring tracks the credits themselves — the one number on this
                        card the user can still move. */}
                    <div className="relative w-12 h-12 shrink-0">
                      <svg viewBox="0 0 36 36" className="w-12 h-12 -rotate-90">
                        <circle cx="18" cy="18" r="15.9155" fill="none" stroke="#f1f5f4" strokeWidth="3.5" />
                        <circle
                          cx="18"
                          cy="18"
                          r="15.9155"
                          fill="none"
                          stroke="#0d9488"
                          strokeWidth="3.5"
                          strokeLinecap="round"
                          strokeDasharray={`${creditsProgress} ${100 - creditsProgress}`}
                          className="transition-all duration-500"
                        />
                      </svg>
                      <span className="absolute inset-0 flex items-center justify-center text-[11px] font-bold text-teal-700">
                        {creditsProgress}%
                      </span>
                    </div>
                  </div>

                  {bonusHint && (
                    <div className="mb-3 flex items-start gap-1.5 rounded-lg bg-amber-50 border border-amber-100 px-2.5 py-1.5">
                      <Sparkles className="w-3 h-3 text-amber-500 shrink-0 mt-0.5" />
                      <p className="text-[11px] text-amber-800 leading-snug">{bonusHint}</p>
                    </div>
                  )}

                  {/* Each reward is a quest: earned, secured (will pay on submit), or
                      still to do — with a jump straight to the step that earns it.
                      Sorted highest-value-first so the biggest win isn't buried at
                      the bottom of the list. */}
                  <ul className="space-y-2 pt-3 border-t border-gray-100">
                    {[...rewardBreakdown]
                      .sort((a, b) => (b.amount || 0) - (a.amount || 0))
                      .map(item => {
                      const secured = !item.unlocked && rewardOnTrack[item.code];
                      const todo = !item.unlocked && !secured;
                      // What this one is actually still waiting on, not a generic
                      // "go to the step that owns it".
                      const gap = todo ? rewardGap(item.code) : null;
                      const action = gap || REWARD_ACTIONS[item.code];
                      return (
                        <li key={item.code} className="flex items-start gap-2.5 text-sm">
                          <span
                            className={`mt-0.5 w-5 h-5 rounded-full shrink-0 flex items-center justify-center ${
                              item.unlocked
                                ? "bg-teal-600 text-white"
                                : secured
                                  ? "bg-amber-400 text-white animate-check-flip"
                                  : "bg-gray-100 text-gray-400"
                            }`}
                          >
                            {item.unlocked ? (
                              <Check className="w-3 h-3" />
                            ) : secured ? (
                              <Sparkles className="w-3 h-3" />
                            ) : (
                              <span className="w-1.5 h-1.5 rounded-full bg-gray-300" />
                            )}
                          </span>
                          <span className="flex-1 min-w-0">
                            <span className={`block text-[13px] font-medium ${todo ? "text-gray-600" : "text-gray-900"}`}>
                              {rewardLabel(item)}
                            </span>
                            {item.unlocked ? (
                              <span className="text-[11px] font-semibold text-teal-600">Earned</span>
                            ) : secured ? (
                              <span className="text-[11px] font-semibold text-amber-600">
                                {PAYS_ON_APPROVAL.has(item.code) ? "Pays once approved" : "Pays on submit"}
                              </span>
                            ) : !isLocked && action ? (
                              <button
                                type="button"
                                onClick={() => goToStep(action.step)}
                                className="text-[11px] font-semibold text-teal-700 hover:text-teal-900 underline underline-offset-2 cursor-pointer"
                              >
                                {action.cta} →
                              </button>
                            ) : null}
                          </span>
                          {/* A rule with no configured amount still lists as a quest,
                              but "+0" reads as a bug — show nothing instead. */}
                          {item.amount > 0 && (
                            <span
                              className={`text-[13px] font-bold tabular-nums shrink-0 ${
                                item.unlocked ? "text-teal-600" : secured ? "text-amber-600" : "text-gray-400"
                              }`}
                            >
                              +{item.amount}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>

                  {/* Real figure from /api/community/stats. The card is omitted rather
                      than showing a placeholder if the request fails, and it reports the
                      period the API actually measures (30 days), not the mock's "week". */}
                  {communityStats?.activeTravelersLast30Days > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100 flex items-start gap-2.5">
                      <span className="w-7 h-7 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center shrink-0">
                        <Users className="w-3.5 h-3.5" />
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-gray-800">Community impact</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {communityStats.activeTravelersLast30Days === 1
                            ? "Helping a fellow traveller who shared a trip in the last 30 days"
                            : `Helping ${communityStats.activeTravelersLast30Days.toLocaleString()} travellers who shared trips in the last 30 days`}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Pro tips for the photo step. Advice only — no invented statistics; the
                one number shown is the credits the API actually pays for photos. */}
            {currentStep === 3 && !isLocked && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                <h3 className="text-sm font-bold text-teal-900 mb-4">Pro tip</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2.5">
                    <Map className="w-4 h-4 text-teal-600 shrink-0 mt-0.5" />
                    <p className="text-xs text-gray-600 leading-relaxed">
                      <span className="font-semibold text-gray-900">Tell a story.</span>{" "}
                      Photos of local interactions and small details often resonate more than
                      just landscapes.
                    </p>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <Check className="w-4 h-4 text-teal-600 shrink-0 mt-0.5" />
                    <p className="text-xs text-gray-600 leading-relaxed">
                      <span className="font-semibold text-gray-900">Stay authentic.</span>{" "}
                      Unedited, raw moments help others trust your review more.
                    </p>
                  </li>
                  <li className="flex items-start gap-2.5">
                    <Sparkles className="w-4 h-4 text-teal-600 shrink-0 mt-0.5" />
                    <p className="text-xs text-gray-600 leading-relaxed">
                      <span className="font-semibold text-gray-900">Caption them.</span>{" "}
                      A line of context under each photo is what turns a gallery into a story.
                    </p>
                  </li>
                </ul>

                {photoBonus?.amount > 0 && !photoBonus.unlocked && (
                  <p className="mt-4 pt-4 border-t border-gray-100 text-xs font-semibold text-amber-700 flex items-start gap-1.5">
                    <Gift className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                    Add one photo to earn +{photoBonus.amount} {rewards?.currency || "RoamPoints"}.
                  </p>
                )}
              </div>
            )}

            {/* Need inspiration — prompts for the story step. */}
            {currentStep === 2 && !isLocked && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                  <Lightbulb className="w-3.5 h-3.5" /> Need inspiration?
                </h3>
                <ul className="space-y-2">
                  {INSPIRATION_PROMPTS.map((item) => (
                    <li
                      key={item.text}
                      className="flex items-start gap-2.5 px-3 py-2.5 rounded-lg border border-gray-100 text-xs text-gray-600"
                    >
                      <item.Icon className="w-3.5 h-3.5 text-teal-600 shrink-0 mt-0.5" />
                      <span>{item.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

          </div>
        </div>
      </main>

      {/* Sticky action bar. Replaces the old in-column button block so the primary
          action, the save state and what the review is worth stay on screen no
          matter how far down a step the user has scrolled. */}
      {!isLocked && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur border-t border-gray-200 shadow-[0_-2px_12px_rgba(0,0,0,0.04)]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-4">
            <button
              type="button"
              onClick={prevStep}
              disabled={currentStep === 1}
              className="flex items-center gap-1.5 text-sm font-semibold text-gray-500 hover:text-gray-900 transition-colors disabled:opacity-0 disabled:pointer-events-none cursor-pointer"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>

            {/* Mirrors the real autosave state rather than always claiming "saved". */}
            <span className="hidden sm:flex items-center gap-1.5 text-xs font-medium" aria-live="polite">
              {saveStatus === "saving" && (
                <span className="flex items-center gap-1.5 text-gray-400">
                  <span className="w-3 h-3 border-2 border-gray-200 border-t-teal-600 rounded-full animate-spin" />
                  Saving…
                </span>
              )}
              {saveStatus === "saved" && (
                <span className="flex items-center gap-1.5 text-gray-400">
                  <Check className="w-3.5 h-3.5 text-teal-600" /> Auto saved
                  {lastSaved && ` ${lastSaved.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`}
                </span>
              )}
              {/* A failed save must stay actionable, not just visible. */}
              {saveStatus === "error" && (
                <span className="flex items-center gap-2 text-red-600">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                  <span className="max-w-[18rem] truncate">{saveErrorMessage || "Not saved"}</span>
                  <button
                    type="button"
                    onClick={() => {
                      clearTimeout(autoSaveTimerRef.current);
                      retryAttemptRef.current = 0;
                      saveDraft(false);
                    }}
                    className="px-2 py-0.5 rounded-md border border-red-200 bg-red-50 text-red-700 text-[11px] font-semibold hover:bg-red-100 transition-colors cursor-pointer"
                  >
                    Retry
                  </button>
                </span>
              )}
            </span>

            <div className="ml-auto flex items-center gap-4">
              {rewards && (
                <div ref={barCreditsRef} className="hidden sm:block text-right">
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider leading-tight">
                    {currentStep === TOTAL_STEPS ? "Ready to claim" : "Secured so far"}
                  </p>
                  {/* On every other step this totals every on-track reward
                      (securedCredits) as a plain progress figure. On the last
                      step "Ready to claim" has to mean what Submit actually pays
                      right now — claimableCredits, the same number the button
                      itself shows — not the fuller total that includes rewards
                      like REVIEW_WITH_PHOTOS that only pay once a moderator
                      approves the post. Showing securedCredits here read as the
                      button shortchanging the traveller by the approval-only
                      portion. */}
                  {(() => {
                    const displayCredits = currentStep === TOTAL_STEPS ? claimableCredits : securedCredits;
                    return (
                      <p className="text-[11px] font-semibold text-teal-700" key={displayCredits}>
                        <span className="inline-block animate-reward-pop">
                          +{displayCredits} {rewards.currency || "RoamPoints"}
                        </span>
                        {currentStep === TOTAL_STEPS && approvalCredits > 0 && (
                          <span className="text-amber-600"> · +{approvalCredits} once approved</span>
                        )}
                        {missedCredits > 0 && (
                          <span className="text-amber-600"> · +{missedCredits} to grab</span>
                        )}
                      </p>
                    );
                  })()}
                </div>
              )}

              <button
                ref={nextButtonRef}
                onClick={currentStep === TOTAL_STEPS ? handleShareClick : nextStep}
                disabled={
                  submitting ||
                  isUploading ||
                  // onSubmitReview already rejects an empty review server-side
                  // (and client-side, before the round trip) — this just makes
                  // that requirement visible upfront instead of only after a
                  // click-then-toast round trip.
                  (currentStep === TOTAL_STEPS && !descriptionContent?.trim())
                }
                title={
                  currentStep === TOTAL_STEPS && !descriptionContent?.trim()
                    ? "Write your review before submitting"
                    : undefined
                }
                className="px-6 py-3 bg-teal-700 text-white font-bold rounded-xl shadow-sm hover:bg-teal-800 transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                {isUploading
                  ? "Uploading photos…"
                  : submitting
                  ? "Submitting…"
                  : currentStep === TOTAL_STEPS
                    ? (
                      <>
                        {claimableCredits > 0
                          ? `Submit & Claim +${claimableCredits} ${rewards?.currency || "RoamPoints"}`
                          : "Submit & Claim Rewards"}
                        <ArrowRight className="w-4 h-4" />
                      </>
                    )
                    : <>Continue <ArrowRight className="w-4 h-4" /></>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* The success dialog was replaced by the full thank-you screen, which
          renders for any submitted review rather than only right after the
          click that submitted it. */}

      {burst > 0 && <CelebrationBurst burstKey={burst} onDone={clearBurst} />}

      {flight && <CoinFlight key={flight.key} flight={flight} onDone={clearFlight} />}

      {showExpiredDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-sm text-center">
            <h3 className="text-xl font-bold mb-2">Draft Expired</h3>
            <p className="mb-6 text-gray-600">This draft is too old. Please start fresh.</p>
            <div className="flex gap-2 justify-center">
              <button onClick={() => setShowExpiredDialog(false)} className="px-4 py-2 bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={handleDeleteDraft} className="px-4 py-2 bg-red-600 text-white rounded-lg">Delete Draft</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
